/* global Excel */

import {
  type SheetStructure,
  type RangeStyle,
  type AnalyzeRangeStyleResult,
} from "excel-agent-core";
import { createSearchPageCollector } from "./search-data-pagination";
import { getStableSheetId, preloadSheetIds } from "./sheet-id-map";

export interface CellData {
  value: string | number | boolean | null;
  formula?: string;
}

export interface CompactStyle {
  b?: boolean;
  i?: boolean;
  sz?: number;
  color?: string;
  family?: string;
  fgColor?: string;
  alignment?: string;
  numFmt?: string;
}

export interface CompactWorksheetInfo {
  name: string;
  dimension: string;
  cells: Record<string, unknown>;
  styles?: Record<string, CompactStyle>;
  borders?: Record<string, string>;
}

export interface GetRangeAsJsonCompactResult {
  success: boolean;
  hasMore: boolean;
  worksheet: CompactWorksheetInfo;
}

// Legacy types kept for internal use
export interface CellStyle {
  sz?: number;
  color?: string;
  family?: string;
  fgColor?: string;
  bold?: boolean;
  italic?: boolean;
}

function columnIndexToLetter(index: number): string {
  let letter = "";
  let temp = index;
  while (temp >= 0) {
    letter = String.fromCharCode((temp % 26) + 65) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
}

function cellAddress(rowIndex: number, colIndex: number): string {
  return `${columnIndexToLetter(colIndex)}${rowIndex + 1}`;
}

function parseRangeAddress(address: string): {
  startCol: number;
  startRow: number;
} {
  const clean = address.split("!").pop()?.split(":")[0] || "A1";
  const match = clean.match(/([A-Z]+)(\d+)/);
  if (!match) return { startCol: 0, startRow: 0 };
  const col =
    match[1].split("").reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) -
    1;
  const row = Number.parseInt(match[2], 10) - 1;
  return { startCol: col, startRow: row };
}

function excelColorToHex(
  color: Excel.RangeFont | Excel.RangeFill,
): string | undefined {
  const c = color as { color?: string };
  if (!c.color || c.color === "null") return undefined;
  if (c.color.startsWith("#")) return c.color.toUpperCase();
  return c.color;
}

async function getPointsPerStandardColumnWidth(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
): Promise<number> {
  const probe = sheet.getRange("XFD:XFD");
  probe.format.load("columnWidth");
  sheet.load("standardWidth");
  await context.sync();

  const originalWidth = probe.format.columnWidth;
  probe.format.useStandardWidth = true;
  probe.format.load("columnWidth");
  await context.sync();

  const standardWidthPoints = probe.format.columnWidth;
  probe.format.columnWidth = originalWidth;
  await context.sync();

  if (sheet.standardWidth && standardWidthPoints) {
    return standardWidthPoints / sheet.standardWidth;
  }
  return 1;
}

export async function getWorksheetById(
  context: Excel.RequestContext,
  sheetId: number,
): Promise<Excel.Worksheet | null> {
  const sheets = context.workbook.worksheets;
  sheets.load("items");
  await context.sync();

  for (const sheet of sheets.items) {
    sheet.load("id");
  }
  await context.sync();

  const idMap = await preloadSheetIds(sheets.items);

  for (const sheet of sheets.items) {
    const stableId = idMap.get(sheet.id);
    if (stableId === sheetId) {
      return sheet;
    }
  }

  return null;
}

export async function getWorksheetStableId(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
): Promise<number> {
  sheet.load("id");
  await context.sync();
  return getStableSheetId(sheet.id);
}

// ─── Style Grouping Utilities ──────────────────────────────────────────────

function parseCellAddr(addr: string): { row: number; col: number } {
  const m = addr.match(/^([A-Z]+)(\d+)$/);
  if (!m) return { row: 0, col: 0 };
  const col = m[1].split("").reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1;
  return { row: parseInt(m[2], 10), col };
}

function groupByIdenticalValue<T>(perCell: Record<string, T>): Record<string, T> {
  const byHash = new Map<string, { value: T; cells: { row: number; col: number }[] }>();

  for (const [addr, val] of Object.entries(perCell)) {
    const hash = JSON.stringify(val, Object.keys(val as object).sort());
    const entry = byHash.get(hash);
    if (entry) {
      entry.cells.push(parseCellAddr(addr));
    } else {
      byHash.set(hash, { value: val, cells: [parseCellAddr(addr)] });
    }
  }

  const result: Record<string, T> = {};
  for (const { value, cells } of byHash.values()) {
    const rangeKeys = mergeIntoRectangles(cells);
    for (const key of rangeKeys) {
      result[key] = value;
    }
  }
  return result;
}

function mergeIntoRectangles(cells: { row: number; col: number }[]): string[] {
  if (cells.length === 0) return [];
  if (cells.length === 1) {
    return [cellAddress(cells[0].row - 1, cells[0].col)];
  }

  cells.sort((a, b) => a.row - b.row || a.col - b.col);
  const set = new Set(cells.map(c => `${c.row},${c.col}`));
  const visited = new Set<string>();
  const ranges: string[] = [];

  for (const cell of cells) {
    const key = `${cell.row},${cell.col}`;
    if (visited.has(key)) continue;

    let maxCol = cell.col;
    while (set.has(`${cell.row},${maxCol + 1}`) && !visited.has(`${cell.row},${maxCol + 1}`)) {
      maxCol++;
    }

    let maxRow = cell.row;
    let canExtend = true;
    while (canExtend) {
      const nextRow = maxRow + 1;
      for (let c = cell.col; c <= maxCol; c++) {
        if (!set.has(`${nextRow},${c}`) || visited.has(`${nextRow},${c}`)) {
          canExtend = false;
          break;
        }
      }
      if (canExtend) maxRow = nextRow;
    }

    for (let r = cell.row; r <= maxRow; r++) {
      for (let c = cell.col; c <= maxCol; c++) {
        visited.add(`${r},${c}`);
      }
    }

    const topLeft = cellAddress(cell.row - 1, cell.col);
    if (cell.row === maxRow && cell.col === maxCol) {
      ranges.push(topLeft);
    } else {
      ranges.push(`${topLeft}:${cellAddress(maxRow - 1, maxCol)}`);
    }
  }

  return ranges;
}

function borderToShorthand(border: {
  top?: { style?: string; color?: string };
  bottom?: { style?: string; color?: string };
  left?: { style?: string; color?: string };
  right?: { style?: string; color?: string };
}): string | null {
  const sides = [border.top, border.bottom, border.left, border.right];
  const validSides = sides.filter(s => s && s.style && s.style !== "None");
  if (validSides.length === 0) return null;

  const allSame = validSides.every(
    s => s!.style === validSides[0]!.style && s!.color === validSides[0]!.color,
  );
  if (allSame && validSides.length === 4) {
    return `${validSides[0]!.style} ${validSides[0]!.color || "#000000"}`;
  }

  const parts: string[] = [];
  const names = ["top", "bottom", "left", "right"] as const;
  for (let i = 0; i < 4; i++) {
    const s = sides[i];
    if (s && s.style && s.style !== "None") {
      parts.push(`${names[i]}: ${s.style} ${s.color || "#000000"}`);
    }
  }
  return parts.join("; ");
}

// ─── Main Read Function ────────────────────────────────────────────────────

export async function getRangeAsJsonCompact(
  sheetId: number,
  ranges: string[],
  options: { includeStyles?: boolean; cellLimit?: number } = {},
): Promise<GetRangeAsJsonCompactResult> {
  const { includeStyles = false, cellLimit = 2000 } = options;

  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) {
      throw new Error(`Worksheet with ID ${sheetId} not found`);
    }

    sheet.load("name,id");
    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load("address");
    await context.sync();

    const dimension = usedRange.isNullObject
      ? "A1"
      : usedRange.address.split("!")[1] || "A1";

    const cells: Record<string, unknown> = {};
    const perCellStyles: Record<string, CompactStyle> = {};
    const perCellBorders: Record<string, {
      top?: { style?: string; color?: string };
      bottom?: { style?: string; color?: string };
      left?: { style?: string; color?: string };
      right?: { style?: string; color?: string };
    }> = {};
    let totalCells = 0;
    let hasMore = false;

    for (const rangeAddr of ranges) {
      if (totalCells >= cellLimit) {
        hasMore = true;
        break;
      }

      const range = sheet.getRange(rangeAddr);
      range.load("values,formulas,address,rowCount,columnCount");
      await context.sync();

      const startAddress = range.address.split("!")[1]?.split(":")[0] || "A1";
      const startMatch = startAddress.match(/([A-Z]+)(\d+)/);
      const startCol = startMatch
        ? startMatch[1]
          .split("")
          .reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1
        : 0;
      const startRow = startMatch ? Number.parseInt(startMatch[2], 10) - 1 : 0;

      const styleTargetsMap = new Map<string, Excel.Range>();

      for (let r = 0; r < range.rowCount; r++) {
        let rowCellCount = 0;
        for (let c = 0; c < range.columnCount; c++) {
          const value = range.values[r][c];
          const formula = range.formulas[r][c];
          const hasValue = value !== null && value !== "" && value !== undefined;
          const hasFormula = typeof formula === "string" && formula.startsWith("=");

          if (!hasValue && !hasFormula) continue;

          rowCellCount++;
        }

        if (totalCells + rowCellCount > cellLimit) {
          hasMore = true;
          break;
        }

        for (let c = 0; c < range.columnCount; c++) {
          const addr = cellAddress(startRow + r, startCol + c);
          const value = range.values[r][c];
          const formula = range.formulas[r][c];
          const hasValue = value !== null && value !== "" && value !== undefined;
          const hasFormula = typeof formula === "string" && formula.startsWith("=");

          if (!hasValue && !hasFormula) continue;

          if (hasFormula) {
            cells[addr] = [hasValue ? value : null, formula];
          } else {
            cells[addr] = value;
          }
          totalCells++;

          if (includeStyles) {
            styleTargetsMap.set(addr, range.getCell(r, c));
          }
        }
      }

      if (includeStyles && styleTargetsMap.size > 0) {
        const styleTargets = Array.from(styleTargetsMap.entries());
        const BATCH_SIZE = 50;
        for (let batchStart = 0; batchStart < styleTargets.length; batchStart += BATCH_SIZE) {
          const batch = styleTargets.slice(batchStart, batchStart + BATCH_SIZE);

          // Store border proxy references during load phase — each getItem() creates
          // a new proxy, so we must reuse the same objects when reading values.
          const borderProxies = new Map<string, {
            top: Excel.RangeBorder;
            bottom: Excel.RangeBorder;
            left: Excel.RangeBorder;
            right: Excel.RangeBorder;
          }>();

          for (const [addr, cell] of batch) {
            cell.format.font.load("name,size,color,bold,italic");
            cell.format.fill.load("color");
            cell.format.load("horizontalAlignment");
            cell.load("numberFormat");
            const topB = cell.format.borders.getItem("EdgeTop");
            const bottomB = cell.format.borders.getItem("EdgeBottom");
            const leftB = cell.format.borders.getItem("EdgeLeft");
            const rightB = cell.format.borders.getItem("EdgeRight");
            topB.load("style,color");
            bottomB.load("style,color");
            leftB.load("style,color");
            rightB.load("style,color");
            borderProxies.set(addr, { top: topB, bottom: bottomB, left: leftB, right: rightB });
          }
          await context.sync();

          for (const [addr, cell] of batch) {
            const style: CompactStyle = {};
            const font = cell.format.font;
            const fill = cell.format.fill;

            if (font.bold) style.b = true;
            if (font.italic) style.i = true;
            if (font.size) style.sz = font.size;
            if (font.name) style.family = font.name;

            const fontColor = excelColorToHex(font as Excel.RangeFont);
            if (fontColor) style.color = fontColor;
            const fillColor = excelColorToHex(fill as Excel.RangeFill);
            if (fillColor) style.fgColor = fillColor;

            const hAlign = cell.format.horizontalAlignment;
            if (hAlign && hAlign !== "General") style.alignment = hAlign.toLowerCase();

            const numFmtRaw = cell.numberFormat;
            const numFmt = Array.isArray(numFmtRaw) ? numFmtRaw[0]?.[0] : numFmtRaw;
            if (numFmt && numFmt !== "General" && numFmt !== "@") {
              style.numFmt = typeof numFmt === "string" ? numFmt : String(numFmt);
            }

            if (Object.keys(style).length > 0) {
              perCellStyles[addr] = style;
            }

            // Reuse the same proxy objects that were loaded above
            const borders = borderProxies.get(addr)!;
            const borderObj = {
              top: { style: borders.top.style, color: borders.top.color },
              bottom: { style: borders.bottom.style, color: borders.bottom.color },
              left: { style: borders.left.style, color: borders.left.color },
              right: { style: borders.right.style, color: borders.right.color },
            };
            const shorthand = borderToShorthand(borderObj);
            if (shorthand) {
              perCellBorders[addr] = borderObj;
            }
          }
        }
      }
    }

    const groupedStyles = Object.keys(perCellStyles).length > 0
      ? groupByIdenticalValue(perCellStyles)
      : undefined;

    let groupedBorders: Record<string, string> | undefined;
    if (Object.keys(perCellBorders).length > 0) {
      const perCellShorthand: Record<string, string> = {};
      for (const [addr, b] of Object.entries(perCellBorders)) {
        const s = borderToShorthand(b);
        if (s) perCellShorthand[addr] = s;
      }
      if (Object.keys(perCellShorthand).length > 0) {
        groupedBorders = groupByIdenticalValue(perCellShorthand);
      }
    }

    return {
      success: true,
      hasMore,
      worksheet: {
        name: sheet.name,
        dimension,
        cells,
        ...(groupedStyles && { styles: groupedStyles }),
        ...(groupedBorders && { borders: groupedBorders }),
      },
    };
  });
}

export interface GetRangeAsCsvResult {
  success: boolean;
  csv: string;
  rowCount: number;
  columnCount: number;
  hasMore: boolean;
  sheetName: string;
}

export async function getRangeAsCsv(
  sheetId: number,
  rangeAddr: string,
  options: { includeHeaders?: boolean; maxRows?: number } = {},
): Promise<GetRangeAsCsvResult> {
  const { includeHeaders = true, maxRows = 400 } = options;

  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    sheet.load("name");
    const range = sheet.getRange(rangeAddr);
    range.load("values,rowCount,columnCount");
    await context.sync();

    const startRow = includeHeaders ? 0 : 1;
    const availableRows = range.rowCount - startRow;
    const actualRows = Math.min(availableRows, maxRows);
    const hasMore = availableRows > maxRows;

    const rows: string[] = [];
    for (let r = startRow; r < startRow + actualRows; r++) {
      const row = range.values[r].map((v) => {
        if (v === null || v === undefined) return "";
        const str = String(v);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
      rows.push(row.join(","));
    }

    // Token-cost safety net: cap CSV payload at ~30KB (≈22K tokens) so an
    // accidentally wide/long read doesn't blow up the bill. Above this size
    // the LLM should page via hasMore=true or aggregate with formulas
    // (SUM/COUNTIF via eval_officejs) instead of pulling raw data.
    const MAX_CSV_CHARS = 30000;
    let csv = rows.join("\n");
    let truncatedBySize = false;
    if (csv.length > MAX_CSV_CHARS) {
      // Trim to the last full newline before the cap so we never return a
      // half-row that would mislead CSV parsers and the metadata count.
      const slice = csv.slice(0, MAX_CSV_CHARS);
      const lastNewline = slice.lastIndexOf("\n");
      csv = lastNewline > 0 ? slice.slice(0, lastNewline) : slice;
      truncatedBySize = true;
    }

    // When truncated by the size cap, report the row count that actually
    // survived in the CSV — not `actualRows` (which reflects the maxRows
    // limit before size truncation). Otherwise metadata would lie.
    const returnedRowCount = truncatedBySize
      ? (csv.length === 0 ? 0 : csv.split("\n").length)
      : actualRows;

    return {
      success: true,
      csv,
      rowCount: returnedRowCount,
      columnCount: range.columnCount,
      hasMore: hasMore || truncatedBySize,
      sheetName: sheet.name,
    };
  });
}

export interface SearchMatch {
  sheetName: string;
  sheetId: number;
  a1: string;
  value: string | number | boolean;
  formula: string | null;
  row: number;
  column: number;
}

export interface SearchDataResult {
  success: boolean;
  matches: SearchMatch[];
  totalFound: number;
  returned: number;
  offset: number;
  hasMore: boolean;
  searchTerm: string;
  searchScope: string;
  nextOffset: number | null;
  error?: string;
}

export async function searchData(
  searchTerm: string,
  options: {
    sheetId?: number;
    range?: string;
    offset?: number;
    matchCase?: boolean;
    matchEntireCell?: boolean;
    matchFormulas?: boolean;
    useRegex?: boolean;
    maxResults?: number;
  } = {},
): Promise<SearchDataResult> {
  const {
    sheetId,
    range,
    offset = 0,
    matchCase = false,
    matchEntireCell = false,
    matchFormulas = false,
    useRegex = false,
    maxResults = 500,
  } = options;

  const searchScopeLabel = sheetId ? `Sheet ${sheetId}` : "All sheets";

  try {
    return await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items");
      await context.sync();

      for (const sheet of sheets.items) {
        sheet.load("id");
      }
      await context.sync();

      const stableIdMap = await preloadSheetIds(sheets.items);

      const pageCollector = createSearchPageCollector<SearchMatch>(
        offset,
        maxResults,
      );

      let stopSearch = false;

      const sheetsToSearch = sheetId
        ? ([await getWorksheetById(context, sheetId)].filter(
          Boolean,
        ) as Excel.Worksheet[])
        : sheets.items;

      const pattern = useRegex
        ? new RegExp(searchTerm, matchCase ? "" : "i")
        : null;

      for (const sheet of sheetsToSearch) {
        if (stopSearch) break;

        sheet.load("name,id");
        const searchRange = range
          ? sheet.getRange(range)
          : sheet.getUsedRangeOrNullObject();
        // Only request `formulas` when we actually need them.
        // Office.js can return null/throw internally for some ranges when formulas are requested
        // but are not available/loaded.
        const loadProps = matchFormulas
          ? "values,formulas,address,rowCount,columnCount"
          : "values,address,rowCount,columnCount";
        searchRange.load(loadProps);
        await context.sync();

        if (searchRange.isNullObject) continue;

        const { startCol, startRow } = parseRangeAddress(searchRange.address);
        const stableSheetId =
          stableIdMap.get(sheet.id) || (await getStableSheetId(sheet.id));

        for (let r = 0; r < searchRange.rowCount && !stopSearch; r++) {
          for (let c = 0; c < searchRange.columnCount; c++) {
            // Some Excel ranges can return `null`/empty `values` or `formulas`.
            // Guard access to avoid: "Cannot read properties of null (reading '0')".
            const value = searchRange.values?.[r]?.[c];
            const formula = matchFormulas ? searchRange.formulas?.[r]?.[c] : undefined;
            const searchTarget =
              matchFormulas && formula != null && formula !== ""
                ? String(formula)
                : String(value ?? "");

            let isMatch = false;
            if (pattern) {
              isMatch = pattern.test(searchTarget);
            } else {
              const compareVal = matchCase
                ? searchTarget
                : searchTarget.toLowerCase();
              const compareTerm = matchCase
                ? searchTerm
                : searchTerm.toLowerCase();
              isMatch = matchEntireCell
                ? compareVal === compareTerm
                : compareVal.includes(compareTerm);
            }

            if (!isMatch) continue;

            const shouldStop = pageCollector.add({
              sheetName: sheet.name,
              sheetId: stableSheetId,
              a1: cellAddress(startRow + r, startCol + c),
              value: (value ?? "") as string | number | boolean,
              formula:
                typeof formula === "string" && formula.startsWith("=") ? formula : null,
              row: startRow + r + 1,
              column: startCol + c + 1,
            });

            if (shouldStop) {
              stopSearch = true;
              break;
            }
          }
        }
      }

      const page = pageCollector.toPage();

      return {
        success: true,
        matches: pageCollector.matches,
        totalFound: page.totalFound,
        returned: page.returned,
        offset: page.offset,
        hasMore: page.hasMore,
        searchTerm,
        searchScope: searchScopeLabel,
        nextOffset: page.nextOffset,
      };
    });
  } catch (err: unknown) {
    const message =
      err instanceof Error ? (err.stack || err.message) : String(err);
    return {
      success: false,
      matches: [],
      totalFound: 0,
      returned: 0,
      offset,
      hasMore: false,
      searchTerm,
      searchScope: searchScopeLabel,
      nextOffset: null,
      error: message,
    };
  }
}

export interface ExcelObject {
  id: string;
  type: "chart" | "pivotTable";
  name: string;
  sheetId: number;
  sheetName: string;
}

export interface GetAllObjectsResult {
  success: boolean;
  objects: ExcelObject[];
}

export async function getAllObjects(
  options: { sheetId?: number; id?: string } = {},
): Promise<GetAllObjectsResult> {
  const { sheetId, id } = options;

  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items");
    await context.sync();

    for (const sheet of sheets.items) {
      sheet.load("id");
    }
    await context.sync();

    const stableIdMap = await preloadSheetIds(sheets.items);

    const objects: ExcelObject[] = [];
    const sheetsToCheck = sheetId
      ? ([await getWorksheetById(context, sheetId)].filter(
        Boolean,
      ) as Excel.Worksheet[])
      : sheets.items;

    for (const sheet of sheetsToCheck) {
      sheet.load("name,id");
      const charts = sheet.charts;
      const pivotTables = sheet.pivotTables;
      charts.load("items");
      pivotTables.load("items");
      await context.sync();

      const stableSheetId =
        stableIdMap.get(sheet.id) || (await getStableSheetId(sheet.id));

      for (const chart of charts.items) {
        chart.load("id,name");
        await context.sync();
        if (!id || chart.id === id) {
          objects.push({
            id: chart.id,
            type: "chart",
            name: chart.name,
            sheetId: stableSheetId,
            sheetName: sheet.name,
          });
        }
      }

      for (const pivot of pivotTables.items) {
        pivot.load("id,name");
        await context.sync();
        if (!id || pivot.id === id) {
          objects.push({
            id: pivot.id,
            type: "pivotTable",
            name: pivot.name,
            sheetId: stableSheetId,
            sheetName: sheet.name,
          });
        }
      }
    }

    return { success: true, objects };
  });
}

export interface CellInput {
  value?: unknown;
  formula?: string;
  note?: string;
  cellStyles?: {
    fontWeight?: "normal" | "bold";
    fontStyle?: "normal" | "italic";
    fontLine?: "none" | "underline" | "line-through";
    fontSize?: number;
    fontFamily?: string;
    fontColor?: string;
    backgroundColor?: string;
    horizontalAlignment?: "left" | "center" | "right";
    numberFormat?: string;
  };
  borderStyles?: {
    top?: { style?: string; weight?: string; color?: string };
    bottom?: { style?: string; weight?: string; color?: string };
    left?: { style?: string; weight?: string; color?: string };
    right?: { style?: string; weight?: string; color?: string };
  };
}

export interface SetCellRangeResult {
  success: boolean;
  cellsWritten: number;
  formulaResults?: Record<string, unknown>;
  messages?: string[];
}

export async function setCellRange(
  sheetId: number,
  rangeAddr: string,
  cells: CellInput[][],
  options: {
    copyToRange?: string;
    resizeWidth?: { type: "points" | "standard"; value: number };
    resizeHeight?: { type: "points" | "standard"; value: number };
    allowOverwrite?: boolean;
  } = {},
): Promise<SetCellRangeResult> {
  const { copyToRange, resizeWidth, resizeHeight, allowOverwrite } = options;

  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    const messages: string[] = [];
    let range = sheet.getRange(rangeAddr);
    range.load("rowCount,columnCount,values,formulas,address");
    await context.sync();

    const inputRows = cells.length;
    const inputCols = Math.max(...cells.map((r) => r.length));
    if (inputRows !== range.rowCount || inputCols !== range.columnCount) {
      // Distinguish three cases:
      //   - Extension: data fits in same orientation, just bigger → allow with warning (current behavior)
      //   - Flip: one dim grew while the other shrank → REJECT (silent reshape would corrupt data)
      //   - Undersized: both dimensions smaller than range → REJECT (would leave cells empty)
      const isFlip =
        (inputRows > range.rowCount && inputCols < range.columnCount) ||
        (inputRows < range.rowCount && inputCols > range.columnCount);
      const isUndersized =
        inputRows < range.rowCount && inputCols < range.columnCount;

      if (isFlip) {
        const startColLetter = parseRangeAddress(range.address);
        const startCol = startColLetter.startCol;
        const startRow = startColLetter.startRow;
        const verticalEndRow = startRow + inputCols - 1;
        const verticalSuggestion = `${cellAddress(startRow, startCol)}:${cellAddress(verticalEndRow, startCol)}`;
        const horizontalEndCol = startCol + inputCols - 1;
        const horizontalSuggestion = `${cellAddress(startRow, startCol)}:${cellAddress(startRow, horizontalEndCol)}`;
        throw new Error(
          `Dimension mismatch: cells array is ${inputRows}×${inputCols} (rows×cols) ` +
          `but range ${rangeAddr} is ${range.rowCount}×${range.columnCount}. ` +
          `Your cells are oriented differently from the target range. ` +
          `If you wanted to write ${inputCols} values into a single column (vertical), use range "${verticalSuggestion}". ` +
          `If you wanted to write ${inputCols} values into a single row (horizontal), use range "${horizontalSuggestion}". ` +
          `Or use write_cells with explicit cell addresses (e.g. {"G14": {formula: "..."}, "G15": {formula: "..."}}) to avoid dimension confusion entirely.`
        );
      }

      if (isUndersized) {
        throw new Error(
          `Dimension mismatch: cells array is ${inputRows}×${inputCols} (smaller than range ${rangeAddr} which is ${range.rowCount}×${range.columnCount}). ` +
          `Either provide more cells or shrink the range to match.`
        );
      }

      // Extension case: allow with a clear warning
      const { startCol, startRow } = parseRangeAddress(range.address);
      const endRow = startRow + inputRows - 1;
      const endCol = startCol + inputCols - 1;
      const newAddr = `${cellAddress(startRow, startCol)}:${cellAddress(endRow, endCol)}`;
      messages.push(
        `Range extended from ${rangeAddr} to ${newAddr} to fit cells (added ${inputRows - range.rowCount} rows, ${inputCols - range.columnCount} cols)`,
      );
      range = sheet.getRange(newAddr);
      range.load("rowCount,columnCount,values,formulas,address");
      await context.sync();
    }

    if (!allowOverwrite) {
      const nonEmptyCells: string[] = [];
      const { startCol, startRow } = parseRangeAddress(range.address);

      for (let r = 0; r < range.rowCount; r++) {
        for (let c = 0; c < range.columnCount; c++) {
          const value = range.values[r][c];
          const formula = range.formulas[r][c];
          const hasValue =
            value !== null && value !== "" && value !== undefined;
          const hasFormula =
            typeof formula === "string" && formula.startsWith("=");

          if (hasValue || hasFormula) {
            nonEmptyCells.push(cellAddress(startRow + r, startCol + c));
          }
        }
      }

      if (nonEmptyCells.length > 0) {
        const cellList =
          nonEmptyCells.length <= 10
            ? nonEmptyCells.join(", ")
            : `${nonEmptyCells.slice(0, 10).join(", ")}...`;
        throw new Error(
          `Would overwrite ${nonEmptyCells.length} non-empty cell(s): ${cellList}. ` +
          `To proceed with overwriting existing data, retry with allow_overwrite set to true.`,
        );
      }
    }

    const values: unknown[][] = [];
    const formulas: (string | null)[][] = [];
    let hasFormulas = false;

    for (let r = 0; r < cells.length; r++) {
      values[r] = [];
      formulas[r] = [];
      for (let c = 0; c < cells[r].length; c++) {
        const cell = cells[r][c];
        if (cell.formula) {
          formulas[r][c] = cell.formula;
          values[r][c] = null;
          hasFormulas = true;
        } else {
          // If the LLM provides a raw value instead of a { value } object, use it directly.
          // If it is an object without a value/formula, it might just be for styles/notes, so use null.
          values[r][c] = (cell as any).value !== undefined ? (cell as any).value : (typeof cell === 'object' ? null : cell);
          formulas[r][c] = null;
        }
      }
    }

    if (hasFormulas) {
      range.formulas = formulas.map((row, r) =>
        row.map((f, c) => f ?? values[r][c]),
      );
    } else {
      range.values = values;
    }

    for (let r = 0; r < cells.length; r++) {
      for (let c = 0; c < cells[r].length; c++) {
        const cell = cells[r][c];
        if (!cell.cellStyles && !cell.borderStyles && !cell.note) continue;

        const cellRange = range.getCell(r, c);

        if (cell.cellStyles) {
          const s = cell.cellStyles;
          if (s.fontWeight === "bold") cellRange.format.font.bold = true;
          if (s.fontWeight === "normal") cellRange.format.font.bold = false;
          if (s.fontStyle === "italic") cellRange.format.font.italic = true;
          if (s.fontStyle === "normal") cellRange.format.font.italic = false;
          if (s.fontLine === "underline")
            cellRange.format.font.underline = "Single";
          if (s.fontLine === "line-through") {
            cellRange.format.font.strikethrough = true;
            cellRange.format.font.underline = "None";
          }
          if (s.fontLine === "none") {
            cellRange.format.font.strikethrough = false;
            cellRange.format.font.underline = "None";
          }
          if (s.fontSize) cellRange.format.font.size = s.fontSize;
          if (s.fontFamily) cellRange.format.font.name = s.fontFamily;
          if (s.fontColor) cellRange.format.font.color = s.fontColor;
          if (s.backgroundColor)
            cellRange.format.fill.color = s.backgroundColor;
          if (s.horizontalAlignment) {
            cellRange.format.horizontalAlignment =
              s.horizontalAlignment as Excel.HorizontalAlignment;
          }
          if (s.numberFormat) cellRange.numberFormat = [[s.numberFormat]];
        }

        if (cell.borderStyles) {
          const b = cell.borderStyles;
          const sideMap: { key: keyof typeof b; index: Excel.BorderIndex }[] = [
            { key: "top", index: Excel.BorderIndex.edgeTop },
            { key: "bottom", index: Excel.BorderIndex.edgeBottom },
            { key: "left", index: Excel.BorderIndex.edgeLeft },
            { key: "right", index: Excel.BorderIndex.edgeRight },
          ];
          for (const { key, index } of sideMap) {
            const side = b[key];
            if (!side) continue;
            const border = cellRange.format.borders.getItem(index);
            if (side.style) {
              const styleMap: Record<string, Excel.BorderLineStyle> = {
                solid: Excel.BorderLineStyle.continuous,
                dashed: Excel.BorderLineStyle.dash,
                dotted: Excel.BorderLineStyle.dot,
                double: Excel.BorderLineStyle.double,
              };
              border.style =
                styleMap[side.style] ?? Excel.BorderLineStyle.continuous;
            }
            if (side.weight) {
              const weightMap: Record<string, Excel.BorderWeight> = {
                thin: Excel.BorderWeight.thin,
                medium: Excel.BorderWeight.medium,
                thick: Excel.BorderWeight.thick,
              };
              border.weight = weightMap[side.weight] ?? Excel.BorderWeight.thin;
            }
            if (side.color) {
              border.color = side.color;
            }
          }
        }

        if (cell.note) {
          cellRange.load("address");
          await context.sync();
          const cellAddr = cellRange.address.split("!")[1] || cellRange.address;
          sheet.notes.add(cellAddr, cell.note);
        }
      }
    }

    await context.sync();

    if (copyToRange) {
      const destRange = sheet.getRange(copyToRange);
      destRange.copyFrom(range, Excel.RangeCopyType.all);
      await context.sync();
    }

    if (resizeWidth) {
      const cols = range.getEntireColumn();
      if (resizeWidth.type === "standard") {
        const pointsPerStandard = await getPointsPerStandardColumnWidth(
          context,
          sheet,
        );
        cols.format.columnWidth = resizeWidth.value * pointsPerStandard;
      } else {
        cols.format.columnWidth = resizeWidth.value;
      }
    } else {
      // Auto-fit column width to content after writing data
      range.getEntireColumn().format.autofitColumns();
    }
    if (resizeHeight) {
      const rows = range.getEntireRow();
      rows.format.rowHeight = resizeHeight.value;
    }

    await context.sync();

    // Fix 3a: use a FRESH range object for the post-write read-back
    // (defensive against stale-read bugs in Office.js)
    const writtenAddress = range.address;
    const verifyRange = sheet.getRange(writtenAddress);
    verifyRange.load(["values", "address"]);
    await context.sync();

    const formulaResults: Record<string, unknown> = {};
    const verificationCells: Record<string, { expected: string | unknown; actual: unknown; ok: boolean }> = {};
    let mismatchCount = 0;

    const { startCol: vStartCol, startRow: vStartRow } = parseRangeAddress(verifyRange.address);
    for (let r = 0; r < verifyRange.values.length; r++) {
      for (let c = 0; c < verifyRange.values[r].length; c++) {
        const addr = cellAddress(vStartRow + r, vStartCol + c);
        const actualValue = verifyRange.values[r][c];
        if (formulas[r]?.[c]) {
          formulaResults[addr] = actualValue;
          verificationCells[addr] = {
            expected: formulas[r][c]!,
            actual: actualValue,
            ok: actualValue !== null && actualValue !== "" && !String(actualValue).startsWith("#"),
          };
          if (!verificationCells[addr].ok) mismatchCount++;
        } else if (values[r]?.[c] !== undefined && values[r]?.[c] !== null) {
          // Non-formula static value writes — verify equality
          const expectedRaw = values[r][c];
          const ok = String(actualValue) === String(expectedRaw);
          verificationCells[addr] = {
            expected: expectedRaw,
            actual: actualValue,
            ok,
          };
          if (!ok) mismatchCount++;
        }
      }
    }

    const verification = Object.keys(verificationCells).length > 0 ? {
      summary: mismatchCount === 0
        ? `All ${Object.keys(verificationCells).length} cell(s) verified — actual values match what was written.`
        : `WARNING: ${mismatchCount} of ${Object.keys(verificationCells).length} cells did NOT match expected values. Investigate.`,
      cells: verificationCells,
    } : undefined;

    return {
      success: true,
      cellsWritten: cells.flat().length,
      ...(Object.keys(formulaResults).length > 0 && { formulaResults }),
      ...(verification && { verification }),
      ...(messages.length > 0 && { messages }),
    };
  });
}

export interface WriteCellsResult {
  written: string[];
  formulaResults: Record<string, unknown>;
  verification?: {
    summary: string;
    cells: Record<string, { expected: string | unknown; actual: unknown; ok: boolean }>;
  };
}

/**
 * Write values or formulas to arbitrary (possibly non-contiguous) cells in one call.
 */
export async function writeCells(
  cells: Record<string, { value?: unknown; formula?: string; numberFormat?: string }>,
  sheetId?: number,
  options: { allowOverwrite?: boolean } = {},
): Promise<WriteCellsResult> {
  const { allowOverwrite = false } = options;

  return Excel.run(async (context: any) => {
    let sheet: any;
    if (sheetId != null) {
      sheet = await getWorksheetById(context, sheetId);
      if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);
    } else {
      sheet = context.workbook.worksheets.getActiveWorksheet();
    }
    const addresses = Object.keys(cells);

    // PHASE 1: Load existing content for overwrite checks (formulas + values)
    const checkRanges = addresses.map(addr => {
      const r = sheet.getRange(addr);
      r.load(["formulas", "values"]);
      return r;
    });
    await context.sync();

    // PHASE 2: Layer 3 overwrite protection — formulas, text, and non-zero numbers
    const overwrites: string[] = [];
    if (!allowOverwrite) {
      for (let i = 0; i < addresses.length; i++) {
        const addr = addresses[i];
        const cell = cells[addr];
        const existingFormula = checkRanges[i].formulas[0][0];
        const existingValue = checkRanges[i].values[0][0];

        // Only check overwrites for STATIC value writes (formulas writes are always allowed)
        const isWritingStatic = cell.value !== undefined && !cell.formula;
        if (!isWritingStatic) continue;

        // Block overwriting an existing formula with a static value
        if (typeof existingFormula === "string" && existingFormula.startsWith("=")) {
          overwrites.push(`${addr} contains formula ${existingFormula} — writing static ${JSON.stringify(cell.value)} would destroy the formula`);
          continue;
        }
        // Block overwriting non-empty text labels
        if (typeof existingValue === "string" && existingValue.trim().length > 0) {
          overwrites.push(`${addr} contains text "${existingValue}" — writing ${JSON.stringify(cell.value)} would replace the label`);
          continue;
        }
        // Block overwriting non-zero numeric content
        if (typeof existingValue === "number" && existingValue !== 0) {
          overwrites.push(`${addr} contains number ${existingValue} — writing ${JSON.stringify(cell.value)} would replace it`);
          continue;
        }
      }
    }

    if (overwrites.length > 0) {
      // Detect calculated-column formula patterns in the formula overwrites.
      // If any overwrite hits a formula that looks like an Excel Table calculated
      // column (VLOOKUP/IFERROR/INDEX/MATCH/XLOOKUP/SUMIFS/=E*F/=*%), append an
      // explicit "EXCEL TABLE DETECTED" hint so the model knows why the formula
      // came back after clearing and that it should skip the cell entirely.
      const CALCULATED_COLUMN_PATTERN = /VLOOKUP|IFERROR|INDEX\s*\(|MATCH\s*\(|XLOOKUP|SUMIFS|=[A-Z]+\d+\s*\*\s*[A-Z]+\d+|=.*\*\s*\d+%/i;
      const likelyCalculatedColumn = overwrites.some(
        o => /contains formula/.test(o) && CALCULATED_COLUMN_PATTERN.test(o),
      );

      let errorMsg =
        `Overwrite protection: ${overwrites.length} cell(s) contain existing data:\n` +
        overwrites.map(o => "  - " + o).join("\n") +
        "\n\nDo NOT proceed without addressing this. Two correct paths:\n" +
        "1. If you intend to ADD new rows: first call modify_sheet_structure with operation:\"insert\" to push existing content down, then retry write_cells with the now-empty addresses.\n" +
        "2. If you genuinely want to replace existing content (e.g. user said \"change F7 to 100\"): confirm with the user first, then retry.\n\n" +
        "DIAGNOSTIC: Your system prompt contains an 'Active Sheet Structure' section with absolute `_row` numbers for every existing data row. Your write target should be AFTER the last `_row` in the relevant region, not INSIDE it. Re-read that section now and identify where existing data actually ends. If the structure looks stale (e.g. you just inserted rows), call analyze_sheet_structure for a fresh scan.";

      if (likelyCalculatedColumn) {
        errorMsg +=
          "\n\nEXCEL TABLE DETECTED: The formulas above (IF/IFERROR/VLOOKUP/INDEX-MATCH/=E*F pattern) look like calculated-column formulas from an Excel Table (ListObject). When you insert a row into a Table and write to any non-calculated input cell (STT, name, quantity, price), Excel AUTOMATICALLY populates the calculated columns using the Table's column formula. You do NOT need to write to these cells — skip them entirely and Excel will fill them in. If you need to verify the calculated value, read the cell AFTER writing the input columns and report its formulaResult. If you already tried to clear the formula and it came back on your next write, that is expected Table auto-fill — do NOT keep clearing. Leave the calculated column alone and only write to the input columns.";
      }

      throw new Error(errorMsg);
    }

    // PHASE 3: Write all cells with FRESH range objects
    // For bulk formula writes (>50 formulas), set calculationMode=manual to prevent
    // Excel from recalculating mid-write. Chained formulas (F5=E5+F4) need all cells
    // to land before recalculation, otherwise intermediate errors cascade.
    const formulaCount = addresses.filter(a => cells[a].formula !== undefined).length;
    const bulkFormulas = formulaCount > 50;
    let savedCalcMode: any;
    if (bulkFormulas) {
      context.application.load("calculationMode");
      await context.sync();
      savedCalcMode = context.application.calculationMode;
      context.application.calculationMode = Excel.CalculationMode.manual;
    }

    const writeRanges = addresses.map(addr => sheet.getRange(addr));
    for (let i = 0; i < addresses.length; i++) {
      const cell = cells[addresses[i]];
      if (cell.formula !== undefined) {
        writeRanges[i].formulas = [[cell.formula]];
      } else if (cell.value !== undefined) {
        writeRanges[i].values = [[cell.value]];
      }
      if (cell.numberFormat) {
        writeRanges[i].numberFormat = [[cell.numberFormat]];
      }
    }

    // Restore calculation mode before sync — ONE recalculation resolves entire chain
    if (bulkFormulas && savedCalcMode !== undefined) {
      context.application.calculationMode = savedCalcMode;
    }

    // Auto-fit written columns (deduplicate so each column is resized only once per sync)
    const uniqueCols = new Set(addresses.map(a => { const m = a.match(/^([A-Z]+)/i); return m ? m[1].toUpperCase() : null; }).filter(Boolean) as string[]);
    for (const col of uniqueCols) {
      sheet.getRange(`${col}:${col}`).format.autofitColumns();
    }

    await context.sync();

    // PHASE 4: Read back values with FRESH range objects (defensive against stale reads)
    const verifyRanges = addresses.map(addr => {
      const r = sheet.getRange(addr);
      r.load("values");
      return r;
    });
    await context.sync();

    const formulaResults: Record<string, unknown> = {};
    const verificationCells: Record<string, { expected: string | unknown; actual: unknown; ok: boolean }> = {};
    let mismatchCount = 0;

    addresses.forEach((addr, i) => {
      const cell = cells[addr];
      const actualValue = verifyRanges[i].values[0][0];
      formulaResults[addr] = actualValue;

      if (cell.formula !== undefined) {
        // Formula write — verify the formula evaluated to a non-error, non-empty value
        const ok = actualValue !== null && actualValue !== "" && !String(actualValue).startsWith("#");
        verificationCells[addr] = {
          expected: cell.formula,
          actual: actualValue,
          ok,
        };
        if (!ok) mismatchCount++;
      } else if (cell.value !== undefined) {
        // Static value write — verify equality
        const ok = String(actualValue) === String(cell.value);
        verificationCells[addr] = {
          expected: cell.value,
          actual: actualValue,
          ok,
        };
        if (!ok) mismatchCount++;
      }
    });

    const verification = {
      summary: mismatchCount === 0
        ? `All ${addresses.length} cell(s) verified — actual values match what was written.`
        : `WARNING: ${mismatchCount} of ${addresses.length} cells did NOT match expected values. Investigate before reporting success.`,
      cells: verificationCells,
    };

    return { written: addresses, formulaResults, verification };
  });
}

/**
 * Reads the active sheet and returns the structural skeleton.
 */

// ---- Table qualification thresholds ----
// A detected region must satisfy BOTH conditions to be reported as a table.
// Regions below these thresholds are discarded (they are likely title rows or
// sparse metadata blocks that were detected as standalone regions by mistake).
const TABLE_MIN_DATA_ROWS = 2;    // minimum number of actual data rows (excluding titles and headers)
const TABLE_MIN_COLUMNS = 2;    // minimum number of detected header columns

export async function analyzeSheetStructureForLLM(sheetId?: number): Promise<SheetStructure> {
  return await Excel.run(async (context: any) => {
    let sheet: Excel.Worksheet;
    if (sheetId) {
      const targeted = await getWorksheetById(context, sheetId);
      if (!targeted) throw new Error(`Worksheet with ID ${sheetId} not found`);
      sheet = targeted;
    } else {
      sheet = context.workbook.worksheets.getActiveWorksheet();
    }
    sheet.load("name");

    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load(["values", "rowIndex", "columnIndex", "numberFormat"]);
    const mergedAreas = usedRange.getMergedAreasOrNullObject();
    mergedAreas.load("areas/items/rowIndex,areas/items/columnIndex,areas/items/rowCount,areas/items/columnCount");
    await context.sync();

    if (usedRange.isNullObject || !usedRange.values || usedRange.values.length === 0) {
      return { sheetName: sheet.name, tableGroups: [] };
    }

    const safeRowIndex = Number(usedRange.rowIndex) || 0;
    const safeColIndex = Number(usedRange.columnIndex) || 0;

    const trimmed = trimDataBoundary(usedRange.values, safeRowIndex, safeColIndex);
    if (!trimmed) return { sheetName: sheet.name, tableGroups: [] };

    const values = trimmed.matrix;
    const baseRow = trimmed.baseRow;
    const baseCol = trimmed.baseCol;

    const regions = findDataRegions(values);

    const regionContexts = regions.map((region) => {
      const excelAddress = getExcelAddress(
        baseRow + region.minRow,
        baseCol + region.minCol,
        baseRow + region.maxRow,
        baseCol + region.maxCol,
      );

      const splitData = splitHeadersAndData(values, region, baseRow, baseCol);

      const headersDict: Record<string, any> = {};
      splitData.columns.forEach(col => {
        headersDict[col.letter] = {
          index: col.index,
          letter: col.letter,
          path: col.path,
          address: col.address,
          components: col.components
        };
      });

      const dataRowCount = (region.maxRow - region.minRow + 1) - splitData._dataStartIdx;

      return {
        address: excelAddress,
        title: splitData.titles.join(" | "),
        totalRows: region.maxRow - region.minRow + 1,
        totalColumns: region.maxCol - region.minCol + 1,
        headers: headersDict,
        _rawSignature: splitData.columns.map((c: any) => c.path).join("|"),
        _dataRowCount: dataRowCount,
        _columnCount: splitData.columns.length,
        sampleDataRows: splitData.sampleDataRows,
        bottomRows: splitData.bottomRows,
      };
    });


    // --- Separate qualifying tables from non-qualifying title/metadata blocks ---
    // Non-qualifying regions are NOT simply dropped: their text content is collected
    // and forwarded as the title of the next qualifying region that follows them
    // (e.g. a merged banner row above a table should appear as that table's title).
    const groupedRegions: any[] = [];
    let pendingTitle = "";   // accumulated text from discarded regions above

    for (const rc of regionContexts) {
      const qualifies =
        rc._dataRowCount >= TABLE_MIN_DATA_ROWS &&
        rc._columnCount >= TABLE_MIN_COLUMNS;

      if (!qualifies) {
        // Collect the text content of this region (use its own title or all non-empty values).
        const textContent = rc.title || "";
        if (textContent) {
          pendingTitle = pendingTitle ? `${pendingTitle} | ${textContent}` : textContent;
        }
        continue;
      }

      // Attach any accumulated pending title from preceding non-table blocks.
      const effectiveTitle = pendingTitle
        ? pendingTitle + (rc.title ? ` | ${rc.title}` : "")
        : rc.title;
      pendingTitle = ""; // reset after consuming

      const signature = rc._rawSignature;
      let existingGroup = groupedRegions.find((g) => g.signature === signature);
      if (!existingGroup) {
        existingGroup = {
          signature,
          tableMetadata: [],
          headers: rc.headers,
          sampleDataRows: rc.sampleDataRows,
          bottomRows: rc.bottomRows,
        };
        groupedRegions.push(existingGroup);
      }

      existingGroup.tableMetadata.push({
        title: effectiveTitle,
        address: rc.address,
        totalRows: rc.totalRows,
        totalColumns: rc.totalColumns,
      });
    }

    groupedRegions.forEach((g) => delete g.signature);
    return { sheetName: sheet.name, tableGroups: groupedRegions };
  });
}


// --- HELPER FUNCTIONS ---

/** Trim a 2D numberFormat array to match the already-trimmed values matrix */
function trimDataBoundaryNF(
  nf: string[][],
  rowOffset: number,
  colOffset: number,
  numRows: number,
  numCols: number,
): string[][] {
  const result: string[][] = [];
  for (let r = 0; r < numRows; r++) {
    const srcRow = nf[rowOffset + r];
    if (!srcRow) {
      result.push(new Array(numCols).fill('General'));
    } else {
      result.push(srcRow.slice(colOffset, colOffset + numCols));
    }
  }
  return result;
}

/**
 * Build a set of "ghost" cell coordinates (row,col relative to trimmed matrix) —
 * these are non-origin cells inside a merged range. They should be skipped when
 * mapping column representatives to avoid duplicating the merged cell's value.
 *
 * Sources: SpreadsheetLLM paper Module 1 boundary detection.
 */
function buildGhostCellSet(
  mergedAreas: any,
  usedRangeRow: number,
  usedRangeCol: number,
  baseRow: number,
  baseCol: number,
): Set<string> {
  const ghosts = new Set<string>();
  if (!mergedAreas || mergedAreas.isNullObject) return ghosts;
  try {
    for (const area of mergedAreas.areas.items) {
      for (let r = area.rowIndex; r < area.rowIndex + area.rowCount; r++) {
        for (let c = area.columnIndex; c < area.columnIndex + area.columnCount; c++) {
          // Skip the origin cell (top-left of merge)
          if (r === area.rowIndex && c === area.columnIndex) continue;
          // Convert to trimmed-matrix coordinates
          const tr = r - baseRow;
          const tc = c - baseCol;
          if (tr >= 0 && tc >= 0) {
            ghosts.add(`${tr},${tc}`);
          }
        }
      }
    }
  } catch (_) {
    // mergedAreas may not have items if no merges exist — safe to ignore
  }
  return ghosts;
}

/**
 * Compute which rows are text-length outliers (> mean + 2*std).
 * Source: dtung8068 `get_length_row` — used in dual-criterion anchor detection.
 */
function computeLengthOutliers(matrix: any[][]): Set<number> {
  if (matrix.length === 0) return new Set();
  const lengths = matrix.map(row =>
    row.reduce((sum: number, v: any) => sum + String(v ?? '').length, 0)
  );
  const mean = lengths.reduce((a: number, b: number) => a + b, 0) / lengths.length;
  const std = Math.sqrt(lengths.reduce((s: number, l: number) => s + (l - mean) ** 2, 0) / lengths.length);
  const result = new Set<number>();
  lengths.forEach((l, i) => { if (l > mean + 2 * std) result.add(i); });
  return result;
}

function isCellEmpty(val: any): boolean {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string') {
    const cleaned = val.replace(/[\s\u200B-\u200D\uFEFF,]/g, '');
    if (cleaned === '') return true;
  }
  return false;
}

function trimDataBoundary(matrix: any[][], startRow: number, startCol: number) {
  let minR = matrix.length;
  let maxR = -1;
  let minC = matrix[0] ? matrix[0].length : 0;
  let maxC = -1;

  for (let r = 0; r < matrix.length; r++) {
    let rowHasData = false;
    for (let c = 0; c < matrix[r].length; c++) {
      if (!isCellEmpty(matrix[r][c])) {
        rowHasData = true;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
      }
    }
    if (rowHasData) {
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
    }
  }

  if (maxR === -1) return null; // Empty sheet

  const trimmedMatrix = [];
  for (let r = minR; r <= maxR; r++) {
    trimmedMatrix.push(matrix[r].slice(minC, maxC + 1));
  }

  // Cast to Number to prevent potential NaN or undefined issues
  return {
    matrix: trimmedMatrix,
    baseRow: Number(startRow || 0) + Number(minR),
    baseCol: Number(startCol || 0) + Number(minC),
  };
}

interface DataRegion {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

function getRowStats(row: any[]) {
  let minC = row.length, maxC = -1;
  let nonEmptyCount = 0;
  let stringCount = 0;
  let numberCount = 0;

  for (let c = 0; c < row.length; c++) {
    let val = row[c];
    if (!isCellEmpty(val)) {
      nonEmptyCount++;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;

      if (typeof val === 'number') {
        numberCount++;
      } else if (typeof val === 'string') {
        let cleaned = val.replace(/[, \s₫$€%]/g, '');
        if (cleaned !== '' && !isNaN(Number(cleaned))) {
          numberCount++;
        } else {
          stringCount++;
        }
      } else if (typeof val === 'boolean') {
        stringCount++;
      }
    }
  }
  return { isEmpty: nonEmptyCount === 0, nonEmptyCount, stringCount, numberCount, minC, maxC };
}

// ---- Structural anchor helpers (SpreadsheetLLM + dtung8068 + DUrayev) ----

function getRowColumnCoverage(row: any[]): number {
  return row.filter(v => !isCellEmpty(v)).length;
}

/** DUrayev: compare rows by value similarity with a threshold */
function areRowsSimilar(row1: any[], row2: any[], threshold = 0.7): boolean {
  const len = Math.max(row1.length, row2.length);
  if (len === 0) return true;
  let matches = 0;
  for (let i = 0; i < len; i++) {
    if (String(row1[i] ?? '').trim() === String(row2[i] ?? '').trim()) matches++;
  }
  return (matches / len) >= threshold;
}

/** dtung8068: majority data type label for a row */
function getDtypeLabel(row: any[]): string {
  const types = row.filter(v => !isCellEmpty(v)).map(v => typeof v);
  if (types.length === 0) return 'empty';
  const counts: Record<string, number> = {};
  types.forEach(t => { counts[t] = (counts[t] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

/**
 * Determine if row `row` is a structural boundary (section split point).
 * Combines 5 signals from SpreadsheetLLM paper + DUrayev + dtung8068 + kingkillery.
 */
/**
 * Detect if a row looks like a "summary/footer row" (cộng tiền hàng, total, sum, VAT, etc.).
 * Pattern: a row where most cells are empty, but one cell contains a label like
 * "total/cộng/tổng/sum/VAT/thuế" and an adjacent cell contains a numeric total.
 * These rows mark the boundary between data and summary sections.
 */
function isSummaryRow(row: any[]): boolean {
  if (!row || row.length === 0) return false;

  const SUMMARY_LABEL_PATTERN = /\b(total|sum|subtotal|grand\s*total|c[ôộ]ng|t[ôổ]ng|thu[êế]|VAT|GTGT|payment|thanh\s*to[áa]n)\b/i;

  let hasLabel = false;
  let hasNumber = false;
  let nonEmptyCount = 0;

  for (const cell of row) {
    if (cell === null || cell === undefined || cell === '') continue;
    nonEmptyCount++;
    if (typeof cell === 'string' && SUMMARY_LABEL_PATTERN.test(cell)) {
      hasLabel = true;
    }
    if (typeof cell === 'number' || (typeof cell === 'string' && /^[\d.,\s]+$/.test(cell.trim()) && cell.trim().length > 0)) {
      hasNumber = true;
    }
  }

  // Summary rows are typically sparse (few non-empty cells) with a label + a number
  return hasLabel && hasNumber && nonEmptyCount <= 4;
}

function isStructuralAnchor(
  matrix: any[][],
  row: number,
  numberFormats?: string[][],
  ghostCells?: Set<string>,
  lengthOutliers?: Set<number>,
): boolean {
  if (row <= 0 || row >= matrix.length - 1) return false;

  const prevRow = matrix[row - 1];
  const currRow = matrix[row];
  const nextRow = matrix[row + 1];

  // Signal 0 (NEW): summary row detection — force split when entering a summary section.
  // This prevents the analyzer from merging the data table with the summary footer.
  if (isSummaryRow(currRow) && !isSummaryRow(prevRow)) return true;

  // Signal 1 (paper): column coverage delta >= 3
  const prevCov = getRowColumnCoverage(prevRow);
  const currCov = getRowColumnCoverage(currRow);
  if (Math.abs(currCov - prevCov) >= 3) return true;

  // Signal 2 (paper): all-string row → has-numbers row (header→data transition)
  const prevStats = getRowStats(prevRow);
  const currStats = getRowStats(currRow);
  if (prevStats.numberCount === 0 && prevStats.stringCount > 0 && currStats.numberCount > 0) return true;

  // Signal 3 (DUrayev): dissimilar to both prev AND next neighbor
  if (!areRowsSimilar(prevRow, currRow) && !areRowsSimilar(currRow, nextRow)) return true;

  // Signal 4 (dtung8068 dual-criterion): dtype changes AND this row is a length outlier
  if (lengthOutliers?.has(row) && getDtypeLabel(prevRow) !== getDtypeLabel(currRow)) return true;

  // Signal 5 (kingkillery/paper): numberFormat changes between rows
  if (numberFormats?.[row - 1] && numberFormats?.[row]) {
    const prevFmts = new Set(numberFormats[row - 1].filter(f => f && f !== 'General'));
    const currFmts = new Set(numberFormats[row].filter(f => f && f !== 'General'));
    if (prevFmts.size > 0 && currFmts.size > 0 &&
      [...prevFmts].filter(f => currFmts.has(f)).length === 0) return true;
  }

  return false;
}

/**
 * Original shouldSplitRegion: look ahead for header+data pattern.
 * Extended: also split when a narrow key-value section precedes a wide table.
 */
function shouldSplitRegion(matrix: any[][], startRow: number, currentRegion: any) {
  if (!currentRegion.hasData) return false;

  let limit = Math.min(startRow + 15, matrix.length);
  let foundHeader = false;
  let foundData = false;
  let localGap = 0;
  let regionWidth = currentRegion.maxCol - currentRegion.minCol + 1;

  for (let r = startRow; r < limit; r++) {
    let stats = getRowStats(matrix[r]);

    if (stats.isEmpty) {
      localGap++;
      if (localGap > 4 && !foundData) return false;
      continue;
    }
    localGap = 0;

    if (stats.numberCount > 0) {
      foundData = true;
      break;
    }

    if (stats.stringCount >= 3 || (stats.stringCount >= 2 && stats.nonEmptyCount >= regionWidth * 0.4)) {
      foundHeader = true;
    }
  }

  return foundHeader && foundData;
}

function findDataRegions(matrix: any[][]): DataRegion[] {
  const regions: DataRegion[] = [];
  const rows = matrix.length;
  if (rows === 0) return regions;

  let currentRegion: any = null;
  let emptyGap = 0;

  for (let r = 0; r < rows; r++) {
    const stats = getRowStats(matrix[r]);

    if (stats.isEmpty) {
      if (currentRegion) emptyGap++;
      continue;
    }

    if (!currentRegion) {
      currentRegion = {
        minRow: r, maxRow: r, minCol: stats.minC, maxCol: stats.maxC,
        hasData: stats.numberCount > 0
      };
    } else {
      let shouldSplit = false;

      if (emptyGap >= 10) {
        shouldSplit = true;
      } else if (emptyGap > 0) {
        shouldSplit = shouldSplitRegion(matrix, r, currentRegion);
      }

      if (shouldSplit) {
        regions.push(currentRegion as DataRegion);
        currentRegion = {
          minRow: r, maxRow: r, minCol: stats.minC, maxCol: stats.maxC,
          hasData: stats.numberCount > 0
        };
      } else {
        currentRegion.maxRow = r;
        currentRegion.minCol = Math.min(currentRegion.minCol, stats.minC);
        currentRegion.maxCol = Math.max(currentRegion.maxCol, stats.maxC);
        if (stats.numberCount > 0) {
          currentRegion.hasData = true;
        }
      }
    }
    emptyGap = 0;
  }
  if (currentRegion) regions.push(currentRegion as DataRegion);

  return regions;
}

/** IoU-based NMS to remove overlapping duplicate regions (kingkillery). */
function calculateIoU(a: DataRegion, b: DataRegion): number {
  const iMinR = Math.max(a.minRow, b.minRow), iMaxR = Math.min(a.maxRow, b.maxRow);
  const iMinC = Math.max(a.minCol, b.minCol), iMaxC = Math.min(a.maxCol, b.maxCol);
  if (iMaxR < iMinR || iMaxC < iMinC) return 0;
  const inter = (iMaxR - iMinR + 1) * (iMaxC - iMinC + 1);
  const aA = (a.maxRow - a.minRow + 1) * (a.maxCol - a.minCol + 1);
  const bA = (b.maxRow - b.minRow + 1) * (b.maxCol - b.minCol + 1);
  return inter / (aA + bA - inter);
}

function deduplicateRegions(regions: DataRegion[]): DataRegion[] {
  const keep = regions.map(() => true);
  for (let i = 0; i < regions.length; i++) {
    for (let j = i + 1; j < regions.length; j++) {
      if (!keep[i] || !keep[j]) continue;
      if (calculateIoU(regions[i], regions[j]) > 0.5) {
        const aA = (regions[i].maxRow - regions[i].minRow + 1) * (regions[i].maxCol - regions[i].minCol + 1);
        const bA = (regions[j].maxRow - regions[j].minRow + 1) * (regions[j].maxCol - regions[j].minCol + 1);
        keep[aA >= bA ? j : i] = false;
      }
    }
  }
  return regions.filter((_, i) => keep[i]);
}

function splitHeadersAndData(
  matrix: any[][],
  region: DataRegion,
  baseRow: number,
  baseCol: number,
  numberFormats?: string[][],
  ghostCells?: Set<string>,
) {
  const block: any[][] = [];
  for (let r = region.minRow; r <= region.maxRow; r++) {
    block.push(matrix[r].slice(region.minCol, region.maxCol + 1));
  }

  let startOfHeadersIdx = 0;
  const regionWidth = region.maxCol - region.minCol + 1;

  for (let i = 0; i < Math.min(4, block.length); i++) {
    const nonEmptyCount = block[i].filter((val) => !isCellEmpty(val)).length;
    if (
      nonEmptyCount > 0 &&
      nonEmptyCount < regionWidth * 0.4 &&
      regionWidth > 3
    ) {
      startOfHeadersIdx++;
    } else if (nonEmptyCount >= regionWidth * 0.4 || regionWidth <= 3) {
      break;
    }
  }

  let headerRowCount = 0;
  for (
    let i = startOfHeadersIdx;
    i < Math.min(startOfHeadersIdx + 5, block.length);
    i++
  ) {
    const row = block[i];
    const numberCount = row.filter((val) => {
      if (typeof val === "number") return true;
      if (typeof val === "string") {
        const cleaned = val.replace(/[, \s₫$€]/g, "");
        return cleaned !== "" && !isNaN(Number(cleaned));
      }
      return false;
    }).length;

    const nonEmptyCount = row.filter((val) => !isCellEmpty(val)).length;
    if (nonEmptyCount > 0 && numberCount / nonEmptyCount >= 0.3) break;
    headerRowCount++;
  }
  if (headerRowCount === 0) headerRowCount = 1;

  const absStartRow = baseRow + region.minRow;
  const absStartCol = baseCol + region.minCol;
  const dataStartIdx = startOfHeadersIdx + headerRowCount;

  const rawHeaders = block.slice(startOfHeadersIdx, dataStartIdx);
  const headerStartRowAbs = absStartRow + startOfHeadersIdx;

  const columnsDef = flattenMultiLevelHeaders(
    rawHeaders,
    absStartCol,
    headerStartRowAbs + 1 // +1 for 1-based indexing in addresses
  );

  const sampleRowsRaw = block.slice(dataStartIdx, dataStartIdx + 3);
  const bottomRowsRaw = block.length > dataStartIdx + 3 ? block.slice(-2) : [];

  const titles = block
    .slice(0, startOfHeadersIdx)
    .map((r) => r.filter((c) => !isCellEmpty(c)).join(" "))
    .filter((t) => t.trim() !== "");

  return {
    titles: titles,
    _dataStartIdx: dataStartIdx,      // number of title+header rows consumed
    columns: columnsDef,

    sampleDataRows: formatRowsToObjects(
      sampleRowsRaw,
      absStartRow + dataStartIdx,
      absStartCol,
    ),
    bottomRows: formatRowsToObjects(
      bottomRowsRaw,
      absStartRow + block.length - bottomRowsRaw.length,
      absStartCol,
    ),
  };
}

export async function analyzeRangeStyle(
  sheetId: number,
  rangeAddr: string,
): Promise<AnalyzeRangeStyleResult> {
  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    const range = sheet.getRange(rangeAddr);
    range.load([
      "address",
      "format/font/name",
      "format/font/size",
      "format/font/bold",
      "format/font/italic",
      "format/font/color",
      "format/font/underline",
      "format/fill/color",
      "format/horizontalAlignment",
      "format/verticalAlignment",
      "format/wrapText",
      "numberFormat",
    ]);

    // Borders need to be handled carefully in Office.js. 
    // We'll load the common properties for the whole collection.
    const topBorder = range.format.borders.getItem("EdgeTop");
    const bottomBorder = range.format.borders.getItem("EdgeBottom");
    const leftBorder = range.format.borders.getItem("EdgeLeft");
    const rightBorder = range.format.borders.getItem("EdgeRight");

    topBorder.load(["style", "color"]);
    bottomBorder.load(["style", "color"]);
    leftBorder.load(["style", "color"]);
    rightBorder.load(["style", "color"]);

    await context.sync();

    const styles: Record<string, RangeStyle> = {};

    // Helper to extract style from a range (assuming it's loaded)
    const extractStyle = (r: Excel.Range): RangeStyle => ({
      font: {
        name: r.format.font.name,
        size: r.format.font.size,
        bold: r.format.font.bold,
        italic: r.format.font.italic,
        color: r.format.font.color,
        underline: r.format.font.underline,
      },
      fill: { color: r.format.fill.color },
      borders: {
        top: {
          style: topBorder.style,
          color: topBorder.color,
        },
        bottom: {
          style: bottomBorder.style,
          color: bottomBorder.color,
        },
        left: {
          style: leftBorder.style,
          color: leftBorder.color,
        },
        right: {
          style: rightBorder.style,
          color: rightBorder.color,
        },
      },
      alignment: {
        horizontal: r.format.horizontalAlignment,
        vertical: r.format.verticalAlignment,
        wrapText: r.format.wrapText,
      },
      numberFormat: Object.prototype.toString.call(r.numberFormat) === '[object Array]'
        ? r.numberFormat[0][0]
        : r.numberFormat,
    });

    // Actually, Office.js properties like format.font.bold can return "null" if mixed.
    // We'll return the object directly for the range.
    styles[range.address] = extractStyle(range);

    return {
      address: range.address,
      styles: styles,
    };
  });
}

function flattenMultiLevelHeaders(
  headerRows: any[][],
  absoluteStartCol = 0,
  absoluteStartRow = 1,
  regionMinRow = 0,   // row offset of header block in trimmed matrix (for ghost lookup)
  ghostCells?: Set<string>,
  regionMinCol = 0,   // col offset of region in trimmed matrix (for ghost lookup)
): any[] {
  if (!headerRows || headerRows.length === 0) return [];
  const numCols = headerRows[0].length;
  const numRows = headerRows.length;
  const columns: any[] = [];

  const currentParents = new Array(numRows).fill("");
  const currentParentAddresses = new Array(numRows).fill("");

  const headerEndRow = absoluteStartRow + numRows - 1;

  for (let c = 0; c < numCols; c++) {
    const pathParts: string[] = [];
    const cellAddresses: string[] = [];
    const letter = columnIndexToLetter(absoluteStartCol + c);

    for (let r = 0; r < numRows; r++) {
      // Skip ghost cells (non-origin cells of a merged range)
      // Ghost cells are stored as trimmed-matrix coords: tr = absRow - baseRow, tc = absCol - baseCol
      // Here: matrixRow = regionMinRow + r, matrixCol = regionMinCol + c
      const matrixRow = regionMinRow + r;
      const matrixCol = regionMinCol + c;
      if (ghostCells?.has(`${matrixRow},${matrixCol}`)) continue;

      const cellValue = headerRows[r][c]?.toString().trim() || "";
      const currentCellAddress = `${letter}${absoluteStartRow + r}`;

      if (cellValue !== "") {
        currentParents[r] = cellValue;
        currentParentAddresses[r] = currentCellAddress;
        pathParts.push(cellValue);
        cellAddresses.push(currentCellAddress);
      } else if (currentParents[r] !== "") {
        pathParts.push(currentParents[r]);
        cellAddresses.push(currentParentAddresses[r]);
      }
    }

    const cleanParts: string[] = [];
    const cleanAddresses: string[] = [];

    for (let i = 0; i < pathParts.length; i++) {
      if (i === 0 || pathParts[i] !== pathParts[i - 1]) {
        cleanParts.push(pathParts[i]);
        cleanAddresses.push(cellAddresses[i]);
      }
    }

    const fullColumnAddress = (absoluteStartRow === headerEndRow)
      ? `${letter}${absoluteStartRow}`
      : `${letter}${absoluteStartRow}:${letter}${headerEndRow}`;

    const components = cleanParts.map((part, idx) => ({
      name: part,
      address: cleanAddresses[idx]
    }));

    columns.push({
      index: c,
      letter: letter,
      path: cleanParts.join(" > ") || `Column ${c + 1}`,
      address: fullColumnAddress,
      components: components
    });
  }
  return columns;
}

// ---- SpreadsheetLLM Modules 2 & 3: semantic type, inverted index, DFS aggregation ----

/**
 * Infer semantic type of a cell value.
 * Source: kingkillery `detect_semantic_type` + `categorize_number_format`,
 * extended with Vietnamese patterns (₫, dd/mm/yyyy, 1.555.000 thousands-dot).
 */
function detectSemanticType(value: any, nfs?: string): string {
  if (value === null || value === undefined) return 'Empty';

  // NFS-based detection (most reliable — from loaded Office.js numberFormat 2D array)
  if (nfs && nfs !== 'General' && nfs !== '@') {
    if (/[$€£¥₫]/.test(nfs)) return 'Currency';
    if (nfs.includes('%')) return 'Percentage';
    if (/[yY]{2,}/.test(nfs) && !/[dDmM]/.test(nfs)) return 'Year';
    if (/[yY]{2,}|[dD]{1,4}/.test(nfs)) return 'Date';
    if (/[hH]:[mM]/.test(nfs)) return 'Time';
    if (/[Ee][+\-]/.test(nfs)) return 'Scientific';
  }

  if (typeof value === 'number') {
    if (Number.isInteger(value) && value > 1899 && value < 2101) return 'Year';
    return Number.isInteger(value) ? 'Integer' : 'Float';
  }

  const s = String(value).trim();
  // Vietnamese thousands: 1.555.000 (period as separator, no fractional part)
  if (/^\d{1,3}(\.\d{3})+$/.test(s)) return 'Currency';
  if (/^[\d.,]+\s*₫$/.test(s) || /^₫\s*[\d.,]+$/.test(s)) return 'Currency';
  if (/^[$€£¥]\s*[\d.,]+$/.test(s)) return 'Currency';
  // Vietnamese date: dd/mm/yyyy
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s)) return 'Date';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'Date';
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return 'Time';
  if (/^[\d.,]+ *%$/.test(s)) return 'Percentage';
  if (/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(s)) return 'Email';
  if (/^-?\d+\.?\d*[eE][+\-]\d+$/.test(s)) return 'Scientific';
  if (/^\d{4}$/.test(s) && +s >= 1900 && +s <= 2100) return 'Year';
  if (/^-?\d+$/.test(s)) return 'Integer';
  if (/^-?[\d.,]+$/.test(s)) return 'Float';
  return 'Text';
}

/**
 * Build an inverted index: value → cell address(es) as compact A1:C5 ranges.
 * Source: SpreadsheetLLM paper Module 2 + kingkillery `create_inverted_index_translation`.
 * Uses greedy width-first rectangle packing to produce minimal range strings.
 */
function buildInvertedIndex(
  rows: any[][],
  startRow: number,
  startCol: number,
): Record<string, string> {
  const valueMap = new Map<string, [number, number][]>();
  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < (rows[r]?.length ?? 0); c++) {
      const val = rows[r][c];
      if (isCellEmpty(val)) continue;
      const key = String(val);
      if (!valueMap.has(key)) valueMap.set(key, []);
      valueMap.get(key)!.push([r, c]);
    }
  }

  const result: Record<string, string> = {};
  for (const [val, coords] of valueMap.entries()) {
    const cellSet = new Set(coords.map(([r, c]) => `${r},${c}`));
    const processed = new Set<string>();
    const ranges: string[] = [];

    for (const [r, c] of [...coords].sort((a, b) => a[0] - b[0] || a[1] - b[1])) {
      if (processed.has(`${r},${c}`)) continue;
      let width = 1;
      while (cellSet.has(`${r},${c + width}`) && !processed.has(`${r},${c + width}`)) width++;
      let height = 1;
      outer: while (true) {
        for (let w = 0; w < width; w++) {
          if (!cellSet.has(`${r + height},${c + w}`) || processed.has(`${r + height},${c + w}`)) break outer;
        }
        height++;
      }
      for (let dr = 0; dr < height; dr++)
        for (let dc = 0; dc < width; dc++)
          processed.add(`${r + dr},${c + dc}`);
      const s = `${columnIndexToLetter(startCol + c)}${startRow + r}`;
      const e = `${columnIndexToLetter(startCol + c + width - 1)}${startRow + r + height - 1}`;
      ranges.push(width === 1 && height === 1 ? s : `${s}:${e}`);
    }
    result[val] = ranges.join(',');
  }
  return result;
}

/**
 * DFS flood-fill to aggregate contiguous same-type cells into type→address-list map.
 * Source: dtung8068 `identical_cell_aggregation`.
 */
function aggregateByType(
  rows: any[][],
  startRow: number,
  startCol: number,
  nfs?: string[][],
): Record<string, string[]> {
  const typeGrid = rows.map((row, r) => row.map((val, c) => detectSemanticType(val, nfs?.[r]?.[c])));
  const visited = rows.map(r => r.map(() => false));
  const result: Record<string, string[]> = {};

  function dfs(r: number, c: number, type: string): string[] {
    if (r < 0 || r >= rows.length || c < 0 || c >= (rows[0]?.length ?? 0)) return [];
    if (visited[r][c] || typeGrid[r][c] !== type || isCellEmpty(rows[r][c])) return [];
    visited[r][c] = true;
    const addr = `${columnIndexToLetter(startCol + c)}${startRow + r}`;
    return [addr, ...dfs(r + 1, c, type), ...dfs(r, c + 1, type)];
  }

  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < (rows[r]?.length ?? 0); c++) {
      if (!visited[r][c] && !isCellEmpty(rows[r][c])) {
        const type = typeGrid[r][c];
        const addrs = dfs(r, c, type);
        if (addrs.length > 1) {
          if (!result[type]) result[type] = [];
          result[type].push(...addrs);
        }
      }
    }
  }
  return result;
}

/**
 * Alias top-N frequent long strings as @v1…@vN to reduce token count.
 * Source: kingkillery `generate_common_value_map`.
 */
function buildCommonValueMap(
  invertedIndex: Record<string, string>,
  topN = 5,
  minLen = 4,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(invertedIndex)
      .filter(([k]) => k.length >= minLen && isNaN(Number(k)))
      .sort((a, b) => b[1].split(',').length - a[1].split(',').length)
      .slice(0, topN)
      .map(([k], i) => [`@v${i + 1}`, k])
  );
}

function getExcelAddress(
  minRow: number,
  minCol: number,
  maxRow: number,
  maxCol: number,
): string {
  return `${columnIndexToLetter(minCol)}${minRow + 1}:${columnIndexToLetter(maxCol)}${maxRow + 1}`;
}

function formatRowsToObjects(rows: any[][], startRow: number, startCol: number) {
  return rows.map((row, rIdx) => {
    const obj: Record<string, any> = {};
    obj["_row"] = startRow + rIdx + 1;

    row.forEach((val, cIdx) => {
      if (!isCellEmpty(val)) {
        const letter = columnIndexToLetter(startCol + cIdx);
        obj[letter] = val;
      }
    });
    return obj;
  });
}

export interface ClearCellRangeResult {
  success: boolean;
  clearedRange: string;
}

export async function clearCellRange(
  sheetId: number,
  rangeAddr: string,
  clearType: "contents" | "all" | "formats" = "contents",
): Promise<ClearCellRangeResult> {
  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    const range = sheet.getRange(rangeAddr);

    switch (clearType) {
      case "contents":
        range.clear(Excel.ClearApplyTo.contents);
        break;
      case "formats":
        range.clear(Excel.ClearApplyTo.formats);
        break;
      case "all":
        range.clear(Excel.ClearApplyTo.all);
        break;
    }

    await context.sync();

    return { success: true, clearedRange: rangeAddr };
  });
}

export interface CopyToResult {
  success: boolean;
  source: string;
  destination: string;
}

export async function copyTo(
  sheetId: number,
  sourceRange: string,
  destinationRange: string,
): Promise<CopyToResult> {
  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    const source = sheet.getRange(sourceRange);
    const dest = sheet.getRange(destinationRange);
    dest.copyFrom(source, Excel.RangeCopyType.all);
    await context.sync();

    return {
      success: true,
      source: sourceRange,
      destination: destinationRange,
    };
  });
}

export interface ModifySheetStructureResult {
  success: boolean;
  operation: string;
  ATTENTION_READ_THIS_FIRST?: string;
  newEmptyRange?: string;
  shiftedContent?: { from: string; to: string; delta: number };
}

export async function modifySheetStructure(
  sheetId: number,
  params: {
    operation: "insert" | "delete" | "hide" | "unhide" | "freeze" | "unfreeze";
    dimension: "rows" | "columns";
    reference?: string;
    count?: number;
    position?: "before" | "after";
  },
): Promise<ModifySheetStructureResult> {
  const {
    operation,
    dimension,
    reference,
    count = 1,
    position = "before",
  } = params;

  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    if (operation === "freeze") {
      sheet.freezePanes.freezeAt(sheet.getRange(reference || "A1"));
    } else if (operation === "unfreeze") {
      sheet.freezePanes.unfreeze();
    } else if (reference) {
      const isRow = dimension === "rows";
      let rangeRef: string;
      let afterRangeRef: string;

      if (isRow) {
        const startRow = Number.parseInt(reference, 10);
        const endRow = startRow + count - 1;
        rangeRef = `${startRow}:${endRow}`;
        afterRangeRef = `${startRow + 1}:${endRow + 1}`;
      } else {
        const startCol = letterToColumnIndex(reference);
        const endCol = startCol + count - 1;
        rangeRef = `${reference}:${columnIndexToLetter(endCol)}`;
        afterRangeRef = `${columnIndexToLetter(startCol + 1)}:${columnIndexToLetter(endCol + 1)}`;
      }

      const targetRange = sheet.getRange(rangeRef);

      switch (operation) {
        case "insert": {
          const shiftDir = isRow
            ? Excel.InsertShiftDirection.down
            : Excel.InsertShiftDirection.right;

          if (position === "after") {
            sheet.getRange(afterRangeRef).insert(shiftDir);
          } else {
            targetRange.insert(shiftDir);
          }
          break;
        }
        case "delete":
          targetRange.delete(
            isRow
              ? Excel.DeleteShiftDirection.up
              : Excel.DeleteShiftDirection.left,
          );
          break;
        case "hide":
          if (isRow) {
            targetRange.rowHidden = true;
          } else {
            targetRange.columnHidden = true;
          }
          break;
        case "unhide":
          if (isRow) {
            targetRange.rowHidden = false;
          } else {
            targetRange.columnHidden = false;
          }
          break;
      }
    }

    await context.sync();

    // Build a structured shift result so the LLM knows EXACTLY where its next write should target.
    // Fix 4: ATTENTION_READ_THIS_FIRST is the loudest possible field name to grab the model's attention.
    let attentionMessage: string | undefined;
    let newEmptyRange: string | undefined;
    let shiftedContent: { from: string; to: string; delta: number } | undefined;

    if (reference && (operation === "insert" || operation === "delete")) {
      const isRow = dimension === "rows";

      if (isRow) {
        const startRow = Number.parseInt(reference, 10);
        const actualStart = position === "after" ? startRow + 1 : startRow;
        const endRow = actualStart + count - 1;

        if (operation === "insert") {
          newEmptyRange = `${actualStart}:${endRow}`;
          shiftedContent = {
            from: `row ${actualStart}+`,
            to: `row ${actualStart + count}+`,
            delta: count,
          };
          attentionMessage =
            `INSERTED ${count} EMPTY ROW${count > 1 ? "S" : ""}. ` +
            `Your next write_cells MUST target row${count > 1 ? "s" : ""} ${actualStart}${count > 1 ? `-${endRow}` : ""}, ` +
            `NOT row ${actualStart - 1} or row ${endRow + 1}. ` +
            `Content that was at row ${actualStart}+ has shifted DOWN by ${count} ` +
            `(example: the row that USED TO BE at row ${actualStart} is now at row ${actualStart + count}). ` +
            `Update any SUM/formula ranges that reference shifted rows.`;
        } else {
          const lastDeleted = actualStart + count - 1;
          shiftedContent = {
            from: `row ${lastDeleted + 1}+`,
            to: `row ${actualStart}+`,
            delta: -count,
          };
          attentionMessage =
            `DELETED ${count} ROW${count > 1 ? "S" : ""} (${actualStart}${count > 1 ? `-${lastDeleted}` : ""}). ` +
            `Content that was at row ${lastDeleted + 1}+ has shifted UP by ${count} ` +
            `(example: the row that USED TO BE at row ${lastDeleted + 1} is now at row ${actualStart}). ` +
            `Update any SUM/formula ranges that reference shifted rows.`;
        }
      } else {
        const startCol = letterToColumnIndex(reference);
        const actualStart = position === "after" ? startCol + 1 : startCol;
        const endCol = actualStart + count - 1;
        const startLetter = columnIndexToLetter(actualStart);
        const endLetter = columnIndexToLetter(endCol);

        if (operation === "insert") {
          newEmptyRange = `${startLetter}:${endLetter}`;
          shiftedContent = {
            from: `column ${startLetter}+`,
            to: `column ${columnIndexToLetter(actualStart + count)}+`,
            delta: count,
          };
          attentionMessage =
            `INSERTED ${count} EMPTY COLUMN${count > 1 ? "S" : ""} (${startLetter}${count > 1 ? `-${endLetter}` : ""}). ` +
            `Your next write_cells MUST target column${count > 1 ? "s" : ""} ${startLetter}${count > 1 ? `-${endLetter}` : ""}. ` +
            `Content that was at column ${startLetter}+ has shifted RIGHT by ${count}.`;
        } else {
          const shiftedFromLetter = columnIndexToLetter(endCol + 1);
          shiftedContent = {
            from: `column ${shiftedFromLetter}+`,
            to: `column ${startLetter}+`,
            delta: -count,
          };
          attentionMessage =
            `DELETED ${count} COLUMN${count > 1 ? "S" : ""} (${startLetter}${count > 1 ? `-${endLetter}` : ""}). ` +
            `Content that was at column ${shiftedFromLetter}+ has shifted LEFT by ${count}.`;
        }
      }
    }

    // Field order matters: ATTENTION_READ_THIS_FIRST goes first, then the explicit target,
    // then structured shift info, then standard success/operation.
    return {
      ...(attentionMessage ? { ATTENTION_READ_THIS_FIRST: attentionMessage } : {}),
      ...(newEmptyRange ? { newEmptyRange } : {}),
      ...(shiftedContent ? { shiftedContent } : {}),
      success: true,
      operation,
    };
  });
}

function letterToColumnIndex(letter: string): number {
  return (
    letter
      .toUpperCase()
      .split("")
      .reduce((acc, c) => acc * 26 + c.charCodeAt(0) - 64, 0) - 1
  );
}

export interface ModifyWorkbookStructureResult {
  success: boolean;
  operation: string;
  sheetId?: number;
  sheetName?: string;
}

export async function modifyWorkbookStructure(params: {
  operation: "create" | "delete" | "rename" | "duplicate";
  sheetId?: number;
  sheetName?: string;
  newName?: string;
  tabColor?: string;
}): Promise<ModifyWorkbookStructureResult> {
  const { operation, sheetId, sheetName, newName, tabColor } = params;

  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;

    switch (operation) {
      case "create": {
        // Sanitize and ensure unique sheet name
        sheets.load("items/name");
        await context.sync();
        const existingNames = new Set(sheets.items.map((s) => s.name));
        const finalSheetName = sanitizeSheetName(sheetName || "Sheet", existingNames);

        const newSheet = sheets.add(finalSheetName);
        if (tabColor) newSheet.tabColor = tabColor;
        newSheet.load("id,name");
        await context.sync();
        const newSheetIndex = await getWorksheetStableId(context, newSheet);
        return {
          success: true,
          operation,
          sheetId: newSheetIndex,
          sheetName: newSheet.name,
        };
      }
      case "delete": {
        const sheet = await getWorksheetById(context, sheetId!);
        if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);
        sheet.delete();
        await context.sync();
        return { success: true, operation };
      }
      case "rename": {
        const sheet = await getWorksheetById(context, sheetId!);
        if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

        sheets.load("items/name");
        await context.sync();
        const existingNames = new Set(sheets.items.map((s) => s.name));
        const finalName = sanitizeSheetName(newName!, existingNames);

        sheet.name = finalName;
        await context.sync();
        return { success: true, operation, sheetName: finalName };
      }
      case "duplicate": {
        const sheet = await getWorksheetById(context, sheetId!);
        if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);
        const copy = sheet.copy();

        if (newName) {
          sheets.load("items/name");
          await context.sync();
          const existingNames = new Set(sheets.items.map((s) => s.name));
          copy.name = sanitizeSheetName(newName, existingNames);
        }

        copy.load("id,name");
        await context.sync();
        const copyIndex = await getWorksheetStableId(context, copy);
        return {
          success: true,
          operation,
          sheetId: copyIndex,
          sheetName: copy.name,
        };
      }
    }
  });
}

export interface ResizeRangeResult {
  success: boolean;
}

export async function resizeRange(
  sheetId: number,
  params: {
    range?: string;
    width?: { type: "points" | "standard"; value: number };
    height?: { type: "points" | "standard"; value: number };
  },
): Promise<ResizeRangeResult> {
  const { range, width, height } = params;

  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    const targetRange = range ? sheet.getRange(range) : sheet.getRange();

    if (width) {
      const cols = targetRange.getEntireColumn();
      if (width.type === "standard") {
        const pointsPerStandard = await getPointsPerStandardColumnWidth(
          context,
          sheet,
        );
        cols.format.columnWidth = width.value * pointsPerStandard;
      } else {
        cols.format.columnWidth = width.value;
      }
    }
    if (height) {
      const rows = targetRange.getEntireRow();
      rows.format.rowHeight = height.value;
    }

    await context.sync();
    return { success: true };
  });
}

export interface ModifyObjectResult {
  success: boolean;
  operation: string;
  id?: string;
}

export interface NavigateResult {
  success: boolean;
  sheetName?: string;
  range?: string;
}

export async function navigateTo(
  sheetId: number,
  range?: string,
): Promise<NavigateResult> {
  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    sheet.load("name");
    sheet.activate();

    if (range) {
      const targetRange = sheet.getRange(range);
      targetRange.select();
    }

    await context.sync();

    return {
      success: true,
      sheetName: sheet.name,
      ...(range && { range }),
    };
  });
}

export interface SheetMetadata {
  id: number;
  name: string;
  maxRows: number;
  maxColumns: number;
  frozenRows: number;
  frozenColumns: number;
}

export interface WorkbookMetadata {
  success: boolean;
  fileName: string;
  sheetsMetadata: SheetMetadata[];
  totalSheets: number;
  activeSheetId: number;
  activeSheetName: string;
  selectedRange: string;
}

export async function getWorkbookMetadata(): Promise<WorkbookMetadata> {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    workbook.load("name");
    const sheets = workbook.worksheets;
    sheets.load("items");

    const activeSheet = sheets.getActiveWorksheet();
    activeSheet.load("id,name");

    const selectedRange = workbook.getSelectedRange();
    selectedRange.load("address");

    await context.sync();

    const sheetData: {
      sheet: Excel.Worksheet;
      usedRange: Excel.Range;
      freezeLocation: Excel.Range;
    }[] = [];

    for (const sheet of sheets.items) {
      sheet.load("id,name");
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load("rowCount,columnCount,address");
      const freezeLocation = sheet.freezePanes.getLocationOrNullObject();
      freezeLocation.load("rowCount,columnCount");
      sheetData.push({ sheet, usedRange, freezeLocation });
    }
    await context.sync();

    const stableIdMap = await preloadSheetIds(sheets.items);

    const sheetsMetadata: SheetMetadata[] = await Promise.all(
      sheetData.map(async ({ sheet, usedRange, freezeLocation }) => ({
        id: stableIdMap.get(sheet.id) || (await getStableSheetId(sheet.id)),
        name: sheet.name,
        dimension: usedRange.isNullObject ? 'A1' : usedRange.address.split('!')[1] || 'A1',
        maxRows: usedRange.isNullObject ? 0 : usedRange.rowCount,
        maxColumns: usedRange.isNullObject ? 0 : usedRange.columnCount,
        frozenRows: freezeLocation.isNullObject ? 0 : freezeLocation.rowCount,
        frozenColumns: freezeLocation.isNullObject
          ? 0
          : freezeLocation.columnCount,
      })),
    );

    const activeSheetStableId =
      stableIdMap.get(activeSheet.id) ||
      (await getStableSheetId(activeSheet.id));

    const rangeAddress = selectedRange.address.includes("!")
      ? selectedRange.address.split("!")[1]
      : selectedRange.address;

    console.log("[getWorkbookMetadata] activeSheet.id:", activeSheet.id);
    console.log("[getWorkbookMetadata] activeSheet.name:", activeSheet.name);
    console.log(
      "[getWorkbookMetadata] activeSheet.stableId:",
      activeSheetStableId,
    );
    console.log(
      "[getWorkbookMetadata] selectedRange.address:",
      selectedRange.address,
    );
    console.log("[getWorkbookMetadata] parsed rangeAddress:", rangeAddress);

    return {
      success: true,
      fileName: workbook.name || "Untitled",
      sheetsMetadata,
      totalSheets: sheets.items.length,
      activeSheetId: activeSheetStableId,
      activeSheetName: activeSheet.name,
      selectedRange: rangeAddress,
    };
  });
}

async function clearRowColumnAxis(
  context: Excel.RequestContext,
  axis: Excel.RowColumnPivotHierarchyCollection,
): Promise<void> {
  axis.load("items");
  await context.sync();
  for (const item of axis.items) {
    axis.remove(item);
  }
}

async function clearDataAxis(
  context: Excel.RequestContext,
  axis: Excel.DataPivotHierarchyCollection,
): Promise<void> {
  axis.load("items");
  await context.sync();
  for (const item of axis.items) {
    axis.remove(item);
  }
}

async function applyPivotFields(
  context: Excel.RequestContext,
  pivot: Excel.PivotTable,
  properties: {
    rows?: { field: string }[];
    columns?: { field: string }[];
    values?: { field: string; summarizeBy?: string }[];
  },
  clearExisting = false,
): Promise<void> {
  if (clearExisting) {
    if (properties.rows) {
      await clearRowColumnAxis(context, pivot.rowHierarchies);
    }
    if (properties.columns) {
      await clearRowColumnAxis(context, pivot.columnHierarchies);
    }
    if (properties.values) {
      await clearDataAxis(context, pivot.dataHierarchies);
    }
    await context.sync();
  }

  if (properties.rows) {
    for (const row of properties.rows) {
      const hierarchy = pivot.hierarchies.getItem(row.field);
      pivot.rowHierarchies.add(hierarchy);
    }
  }

  if (properties.columns) {
    for (const column of properties.columns) {
      const hierarchy = pivot.hierarchies.getItem(column.field);
      pivot.columnHierarchies.add(hierarchy);
    }
  }

  if (properties.values) {
    const summarizeMap: Record<string, Excel.AggregationFunction> = {
      sum: Excel.AggregationFunction.sum,
      count: Excel.AggregationFunction.count,
      average: Excel.AggregationFunction.average,
      max: Excel.AggregationFunction.max,
      min: Excel.AggregationFunction.min,
    };
    for (const value of properties.values) {
      const hierarchy = pivot.hierarchies.getItem(value.field);
      const dataHierarchy = pivot.dataHierarchies.add(hierarchy);
      if (value.summarizeBy) {
        dataHierarchy.summarizeBy =
          summarizeMap[value.summarizeBy] ?? Excel.AggregationFunction.sum;
      }
    }
  }
}

// Serialize concurrent chart/pivot creations to prevent Office.js queue saturation
// when the LLM requests multiple objects created in the same batch.
let _objectCreateChain: Promise<unknown> = Promise.resolve();

export async function modifyObject(params: {
  operation: "create" | "update" | "delete";
  sheetId: number;
  objectType: "pivotTable" | "chart";
  id?: string;
  properties?: {
    name?: string;
    source?: string;
    range?: string;
    anchor?: string;
    rows?: { field: string }[];
    columns?: { field: string }[];
    values?: { field: string; summarizeBy?: string }[];
    title?: string;
    chartType?: string;
  };
}): Promise<ModifyObjectResult> {
  const { operation, sheetId, objectType, id, properties } = params;

  if (operation === "create") {
    // Queue creates sequentially — concurrent Excel.run() chart.add() calls
    // can saturate the Office.js request queue and silently hang.
    const createFn = () => _runModifyObject(operation, sheetId, objectType, id, properties);
    const result = _objectCreateChain.then(createFn, createFn);
    _objectCreateChain = result.then(() => null, () => null);
    return result;
  }

  return _runModifyObject(operation, sheetId, objectType, id, properties);
}

async function _runModifyObject(
  operation: "create" | "update" | "delete",
  sheetId: number,
  objectType: "pivotTable" | "chart",
  id: string | undefined,
  properties: {
    name?: string;
    source?: string;
    range?: string;
    anchor?: string;
    rows?: { field: string }[];
    columns?: { field: string }[];
    values?: { field: string; summarizeBy?: string }[];
    title?: string;
    chartType?: string;
  } | undefined,
): Promise<ModifyObjectResult> {
  return Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

    if (objectType === "chart") {
      const charts = sheet.charts;

      switch (operation) {
        case "create": {
          if (!properties?.source || !properties?.chartType) {
            throw new Error("Chart creation requires source and chartType");
          }
          const sourceRange = sheet.getRange(properties.source);
          const chart = charts.add(
            properties.chartType as Excel.ChartType,
            sourceRange,
            Excel.ChartSeriesBy.auto,
          );
          if (properties.title) chart.title.text = properties.title;
          if (properties.anchor) {
            const anchorCell = sheet.getRange(properties.anchor);
            chart.setPosition(anchorCell);
          }
          chart.load("id");
          await context.sync();
          return { success: true, operation, id: chart.id };
        }
        case "update": {
          if (!id) throw new Error("Chart update requires id");
          const chart = charts.getItem(id);
          if (properties?.chartType) {
            chart.chartType = properties.chartType as Excel.ChartType;
          }
          if (properties?.source) {
            const sourceRange = sheet.getRange(properties.source);
            chart.setData(sourceRange, Excel.ChartSeriesBy.auto);
          }
          if (properties?.anchor) {
            const anchorCell = sheet.getRange(properties.anchor);
            chart.setPosition(anchorCell);
          }
          if (properties?.title) chart.title.text = properties.title;
          await context.sync();
          return { success: true, operation, id };
        }
        case "delete": {
          if (!id) throw new Error("Chart delete requires id");
          const chart = charts.getItem(id);
          chart.delete();
          await context.sync();
          return { success: true, operation };
        }
      }
    } else {
      const pivotTables = sheet.pivotTables;

      switch (operation) {
        case "create": {
          if (!properties?.source || !properties?.range) {
            throw new Error("PivotTable creation requires source and range");
          }
          const sourceRange = sheet.getRange(properties.source);
          const destRange = sheet.getRange(properties.range);
          const pivot = sheet.pivotTables.add(
            properties.name || "PivotTable",
            sourceRange,
            destRange,
          );
          if (properties?.rows || properties?.columns || properties?.values) {
            await applyPivotFields(context, pivot, properties);
          }
          pivot.load("id");
          await context.sync();
          return { success: true, operation, id: pivot.id };
        }
        case "update": {
          if (!id) throw new Error("PivotTable update requires id");
          const pivot = pivotTables.getItem(id);
          if (properties?.name) pivot.name = properties.name;
          if (properties?.rows || properties?.columns || properties?.values) {
            await applyPivotFields(context, pivot, properties, true);
          }
          await context.sync();
          return { success: true, operation, id };
        }
        case "delete": {
          if (!id) throw new Error("PivotTable delete requires id");
          const pivot = pivotTables.getItem(id);
          pivot.delete();
          await context.sync();
          return { success: true, operation };
        }
      }
    }

    return { success: false, operation: "unknown" };
  });
}

export interface ImportDataResult {
  success: boolean;
  sheetId: number;
  sheetName: string;
}

export async function importData(
  sheetName: string,
  data: any[][],
  hasHeaders: boolean = true,
  targetSheetId?: number,
): Promise<ImportDataResult> {
  const startTime = performance.now();
  console.log(`[Excel] Starting importData for ${data.length} rows...`);

  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    let targetSheet: Excel.Worksheet;

    if (targetSheetId !== undefined) {
      const sheet = await getWorksheetById(context, targetSheetId);
      if (!sheet) throw new Error(`Worksheet with ID ${targetSheetId} not found`);
      targetSheet = sheet;
      targetSheet.getUsedRange().clear(); // Clear existing content before import
    } else {
      sheets.load("items/name");
      await context.sync();
      const existingNames = new Set(sheets.items.map((s) => s.name));
      const finalSheetName = sanitizeSheetName(sheetName || "Sheet", existingNames);
      targetSheet = sheets.add(finalSheetName);
    }

    const setupDone = performance.now();
    targetSheet.activate();

    const CHUNK_SIZE = 5_000; // rows per context.sync — stays well under Office.js ~5 MB limit

    if (data.length > 0) {
      const rowCount = data.length;
      // Use the widest row to determine colCount
      const colCount = Math.max(...data.map(r => Array.isArray(r) ? r.length : 0));
      if (colCount === 0) throw new Error("Data contains no columns");

      // Normalize jagged 2D array to a rectangular one
      const normalizedData = data.map(row => {
        const r = Array.isArray(row) ? [...row] : [];
        while (r.length < colCount) r.push(null);
        return r.slice(0, colCount);
      });

      const endColLetter = columnIndexToLetter(colCount - 1);

      if (rowCount <= CHUNK_SIZE) {
        // Small enough — single write
        const range = targetSheet.getRange(`A1:${endColLetter}${rowCount}`);
        range.values = normalizedData;
      } else {
        // Large dataset — write in chunks to avoid Office.js payload limit
        console.log(`[Excel] Large import: writing ${rowCount} rows in chunks of ${CHUNK_SIZE}`);
        for (let offset = 0; offset < rowCount; offset += CHUNK_SIZE) {
          const chunk = normalizedData.slice(offset, offset + CHUNK_SIZE);
          const startRow = offset + 1;
          const endRow = startRow + chunk.length - 1;
          const range = targetSheet.getRange(`A${startRow}:${endColLetter}${endRow}`);
          range.values = chunk;
          await context.sync();
        }
      }

      const dataPopulated = performance.now();

      // Header Formatting
      if (hasHeaders) {
        const headerRange = targetSheet.getRange(`A1:${endColLetter}1`);
        headerRange.format.font.bold = true;
        headerRange.format.fill.color = "#F3F2F1"; // Light gray
        headerRange.format.borders.getItem("EdgeBottom").style = "Continuous";
      }

      // Apply borders to the entire table
      const fullRange = targetSheet.getRange(`A1:${endColLetter}${rowCount}`);
      const borders = fullRange.format.borders;
      borders.getItem("EdgeTop").style = "Continuous";
      borders.getItem("EdgeBottom").style = "Continuous";
      borders.getItem("EdgeLeft").style = "Continuous";
      borders.getItem("EdgeRight").style = "Continuous";
      borders.getItem("InsideVertical").style = "Continuous";
      borders.getItem("InsideHorizontal").style = "Continuous";

      // Final polish
      targetSheet.getUsedRange().getEntireColumn().format.autofitColumns();
      const totalTime = performance.now() - startTime;
      console.log(`[Excel] Import complete:
  - Setup/Sheet Check: ${(setupDone - startTime).toFixed(2)}ms
  - Data Populated: ${(dataPopulated - setupDone).toFixed(2)}ms
  - Excel Sync (Bulk Write): ${(performance.now() - dataPopulated).toFixed(2)}ms
  - Total: ${totalTime.toFixed(2)}ms`);
    }

    targetSheet.load("id,name");
    await context.sync();
    const resultSheetId = await getWorksheetStableId(context, targetSheet);

    return {
      success: true,
      sheetId: resultSheetId,
      sheetName: targetSheet.name,
    };
  });
}

/**
 * Append rows to an existing sheet starting at a specific 1-based row number.
 * Used by the paginated INSERT_BATCH flow after the first page has created the sheet.
 */
export async function appendRowsToSheet(
  sheetId: number,
  rows: string[][],
  startRow: number,
): Promise<void> {
  if (rows.length === 0) return;
  const CHUNK_SIZE = 5_000;
  await Excel.run(async (context) => {
    const sheet = await getWorksheetById(context, sheetId);
    if (!sheet) throw new Error(`Worksheet ${sheetId} not found`);

    const colCount = Math.max(...rows.map(r => r.length));
    const endColLetter = columnIndexToLetter(colCount - 1);
    const normalized = rows.map(row => {
      const r = [...row];
      while (r.length < colCount) r.push('');
      return r.slice(0, colCount);
    });

    for (let offset = 0; offset < normalized.length; offset += CHUNK_SIZE) {
      const chunk = normalized.slice(offset, offset + CHUNK_SIZE);
      const r1 = startRow + offset;
      const r2 = r1 + chunk.length - 1;
      sheet.getRange(`A${r1}:${endColLetter}${r2}`).values = chunk;
      await context.sync();
    }
  });
}

/**
 * Ensures a sheet name is safe for Excel (no illegal chars, 31 limit, unique).
 */
function sanitizeSheetName(name: string, existingNames: Set<string>): string {
  // Replace illegal chars: \ / : * ? [ ]
  let baseName = (name || "Sheet").replace(/[\\/:\*\?\[\]]/g, "_").trim();
  // Limit to 31 chars
  if (baseName.length > 31) baseName = baseName.slice(0, 31);

  let finalName = baseName;
  let counter = 1;
  while (existingNames.has(finalName)) {
    const suffix = ` (${counter++})`;
    finalName = baseName.slice(0, 31 - suffix.length) + suffix;
  }
  return finalName;
}

/**
 * Summarize the entire workbook in one call: all sheet names, sizes, headers, and sample data.
 * Returns sheets auto-categorized by Vietnamese accounting naming conventions.
 */
export async function summarizeWorkbook(maxSampleRows: number = 2): Promise<any> {
  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name,items/id");
    await context.sync();

    // Resolve stable sheet IDs (1-based numeric) — same mechanism as getWorkbookMetadata
    const stableIdMap = await preloadSheetIds(sheets.items);

    const result: any[] = [];

    for (const sheet of sheets.items) {
      const sheetId = stableIdMap.get(sheet.id) || (await getStableSheetId(sheet.id));
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load(["rowCount", "columnCount", "rowIndex", "columnIndex"]);
      await context.sync();

      if (usedRange.isNullObject) {
        result.push({ id: sheetId, name: sheet.name, rows: 0, cols: 0, headers: [], sampleData: [], empty: true, category: "empty" });
        continue;
      }

      const totalRows = usedRange.rowCount;
      const totalCols = usedRange.columnCount;
      const readRows = Math.min(1 + maxSampleRows, totalRows);
      const readCols = Math.min(totalCols, 20);

      const topRange = sheet.getRangeByIndexes(usedRange.rowIndex, usedRange.columnIndex, readRows, readCols);
      topRange.load("values");
      await context.sync();

      const values = topRange.values;
      const headers = (values[0] || []).map((v: any) => v !== null && v !== "" ? String(v).trim() : "").filter((h: string) => h !== "");
      const sampleData = values.slice(1).map((row: any[]) => row.map((v: any) => v !== null ? String(v).trim() : ""));

      const nameLower = sheet.name.toLowerCase();
      let category = "other";
      if (nameLower.includes("phatsinh") || nameLower.includes("nkc") || nameLower.includes("nhaplieu")) category = "journal";
      else if (nameLower.includes("socai") || nameLower === "sc") category = "ledger";
      else if (nameLower.includes("cdkt") || nameLower.includes("bangcdkt") || nameLower.includes("kqkd") || nameLower.includes("bckqkd") || nameLower.includes("lctt")) category = "financial_statement";
      else if (nameLower.includes("vat") || nameLower.includes("tndn") || nameLower.includes("thue") || nameLower.includes("tokhai")) category = "tax";
      else if (nameLower.includes("congno") || nameLower.includes("thno") || nameLower.includes("ctietcno") || nameLower.includes("131") || nameLower.includes("331")) category = "sub_ledger";
      else if (nameLower.includes("nxt") || nameLower.includes("kho") || nameLower.includes("chitietz") || nameLower.includes("tonghop") || nameLower.includes("giathanh") || nameLower.includes("z154")) category = "cost_inventory";
      else if (nameLower.includes("cdps") || nameLower.includes("cdtk")) category = "trial_balance";
      else if (nameLower.includes("tscd") || nameLower.includes("khauhao") || nameLower.includes("phanbo")) category = "fixed_assets";
      else if (nameLower.includes("luong") || nameLower.includes("bangluong")) category = "payroll";
      else if (nameLower.includes("menu") || nameLower.includes("quanly") || nameLower.includes("hocnhanh")) category = "navigation";
      else if (nameLower.includes("thongtin") || nameLower.includes("ttdn")) category = "company_info";

      result.push({ id: sheetId, name: sheet.name, rows: totalRows, cols: totalCols, headers, sampleData, category });
    }

    const groups: Record<string, string[]> = {};
    for (const s of result) {
      if (!groups[s.category]) groups[s.category] = [];
      groups[s.category].push(s.name);
    }

    return { totalSheets: result.length, sheets: result, summary: groups };
  });
}
